import React, { useState } from "react";
import { useIncremental } from "@/hooks/useIncremental";
import api from "@/lib/api";
import {
  FileText, DollarSign, ClipboardCheck, Target, ListOrdered, Mail,
  Lock, AlertTriangle, Circle, CheckCircle2, ExternalLink, Download, Scissors, Loader2, RefreshCw, FileCheck2,
  Maximize2, X, Eye, EyeOff, Ruler, Share2, Upload, PenLine, RotateCcw, Sparkles, History, AlignLeft,
} from "lucide-react";
import { toast } from "sonner";
import { EmailSubmission } from "./EmailSubmission";
import { XfaPreview } from "./XfaPreview";
import { MissingDrawingsSpecsBanner } from "./MissingDrawingsSpecsBanner";
import { useAuth } from "@/context/AuthContext";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const STATUS = {
  "Not Started": { c: "text-zinc-400", I: Circle },
  "In Progress": { c: "text-blue-500", I: Circle },
  COMPLETE: { c: "text-emerald-600", I: CheckCircle2 },
  GAP: { c: "text-red-500", I: AlertTriangle },
};

const REQ_STATUS = {
  "Covered": { c: "bg-emerald-50 text-emerald-700 border-emerald-200", I: CheckCircle2 },
  "Partial": { c: "bg-amber-50 text-amber-700 border-amber-200", I: AlertTriangle },
  "GAP": { c: "bg-red-50 text-red-700 border-red-200", I: AlertTriangle },
  "Not Started": { c: "bg-zinc-50 text-zinc-500 border-zinc-200", I: Circle },
};

const Placeholder = ({ stage, what }) => (
  <div className="flex flex-col items-center justify-center h-[380px] text-center">
    <Lock className="h-6 w-6 text-zinc-300 mb-2" />
    <div className="text-sm text-zinc-500">{what} not generated yet</div>
    <div className="text-[11px] text-zinc-400 mt-1">Anvil produces this at Stage {stage}</div>
  </div>
);

/* Deliverable preview — vertical icon+label rail (left) + always-visible horizontal stage bar (top). */
export function PreviewTabs({ deal, reload }) {
  const [tab, setTab] = useState("Shred");
  const TABS = [["Shred", Scissors], ["Capture Plan", Target], ["Outline", ListOrdered], ["Takeoff", Ruler],
    ["Pricing", DollarSign], ["Forms", FileCheck2], ["Proposal", FileText], ["Compliance", ClipboardCheck], ["Email & Submission", Mail]];
  return (
    <div className="bg-white border border-zinc-200 shadow-sm rounded-xl overflow-hidden flex flex-col h-[80vh]" data-testid="preview-card">
      <StageBar deal={deal} activeTab={tab} onJump={setTab} />
      <div className="flex flex-1 min-h-0">
        <div className="w-40 shrink-0 border-r border-border overflow-y-auto bg-muted/40" data-testid="preview-tabs">
          {TABS.map(([t, Icon]) => (
            <button key={t} data-testid={`preview-tab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setTab(t)}
              className={`w-full flex items-center gap-2 px-3 py-3 text-xs font-medium text-left border-l-2 transition-colors ${
                tab === t ? "border-gold-dark text-foreground bg-card" : "border-transparent text-muted-foreground hover:text-foreground hover:bg-accent"}`}>
              <Icon className="h-4 w-4 shrink-0" /> <span className="truncate">{t}</span>
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto p-4 min-w-0" data-testid="preview-body">
          {tab === "Shred" && <Shred deal={deal} reload={reload} />}
          {tab === "Capture Plan" && <Capture deal={deal} />}
          {tab === "Outline" && <Outline deal={deal} />}
          {tab === "Takeoff" && <Takeoff deal={deal} reload={reload} />}
          {tab === "Pricing" && <Pricing deal={deal} reload={reload} />}
          {tab === "Forms" && <Forms deal={deal} reload={reload} />}
          {tab === "Proposal" && <ProposalPdf deal={deal} reload={reload} />}
          {tab === "Compliance" && <Compliance deal={deal} reload={reload} />}
          {tab === "Email & Submission" && <EmailSubmission deal={deal} reload={reload} />}
        </div>
      </div>
    </div>
  );
}

/* Stage -> deliverable tab the top bar jumps to (view-only navigation; completion stays automatic). */
const STAGE_TAB = { "1": "Shred", "2": "Forms", "3": "Capture Plan", "4": "Pricing", "5": "Proposal", "6": "Compliance", "7": "Email & Submission", "7B": "Email & Submission" };
const STEP_NAME = { "1": "Showstopper", "2": "Forms", "3": "Capture / Outline", "4": "Pricing", "5": "Draft", "6": "Compliance", "7": "Submit", "7B": "Bonding" };
const STEP_STYLE = {
  "Not Started": { circle: "bg-white text-zinc-400 border-zinc-200", label: "text-zinc-400" },
  "In Progress": { circle: "bg-blue-50 text-blue-600 border-blue-400", label: "text-blue-600 font-semibold" },
  COMPLETE: { circle: "bg-emerald-500 text-white border-emerald-500", label: "text-emerald-700 font-semibold" },
  GAP: { circle: "bg-red-50 text-red-600 border-red-400", label: "text-red-600 font-semibold" },
  NA: { circle: "bg-zinc-50 text-zinc-300 border-zinc-200 border-dashed", label: "text-zinc-300" },
};

/* Horizontal, always-visible 7+7B tracker pinned across the top. Auto-status; click a step to jump.
   Labels sit under each numbered circle; the active ring renders fully (inner padding, no y-clip).
   7B is conditional: greyed N/A when no bond is required, an active gate when a bond IS required. */
function StageBar({ deal, activeTab, onJump }) {
  const stages = deal.bid_stages || [];
  if (!stages.length) return null;
  const bondReq = deal.bond_required; // true | false | undefined(not yet determined)
  return (
    <div className="border-b border-zinc-200 px-4 pt-3 pb-2 shrink-0 bg-white" data-testid="stage-bar">
      <div className="flex items-center gap-1.5 mb-1 text-[10px] text-zinc-400 font-mono uppercase tracking-wider">
        <Lock className="h-3 w-3" /> Gated 7+7B workflow · auto-advances · click a step to view it
      </div>
      <div className="overflow-x-auto">
        <div className="flex items-start min-w-max px-1 pt-2 pb-1" data-testid="stage-pills">
          {stages.map((s, i) => {
            const isLast = i === stages.length - 1;
            const is7B = s.number === "7B";
            const na = is7B && bondReq === false;
            const status = na ? "NA" : (s.status || "Not Started");
            const sty = STEP_STYLE[status] || STEP_STYLE["Not Started"];
            const target = STAGE_TAB[s.number];
            const isActive = target && target === activeTab;
            const prevDone = i > 0 && stages[i - 1].status === "COMPLETE";
            const label = is7B ? (na ? "Bonding · N/A" : "Bonding") : STEP_NAME[s.number];
            return (
              <React.Fragment key={s.number}>
                <button onClick={() => target && onJump(target)} title={`Stage ${s.number}: ${s.name} — ${na ? "N/A (no bond required)" : s.status}`}
                  data-testid={`stage-pill-${s.number}`}
                  className={`flex flex-col items-center gap-1.5 shrink-0 w-[74px] group ${na ? "opacity-40 grayscale" : ""}`}>
                  <span className={`relative flex items-center justify-center h-8 w-8 rounded-full border-2 text-[11px] font-bold transition-all ${sty.circle} ${isActive ? "ring-2 ring-gold-dark ring-offset-2" : ""} group-hover:scale-105`}>
                    {status === "COMPLETE" ? <CheckCircle2 className="h-4 w-4" /> : status === "GAP" ? <AlertTriangle className="h-4 w-4" /> : <span className={status === "In Progress" ? "animate-pulse" : ""}>{s.number}</span>}
                  </span>
                  <span className={`text-[9px] leading-tight text-center w-full ${sty.label}`}>{s.number} {label}</span>
                </button>
                {!isLast && <span className={`h-0.5 w-6 mt-4 shrink-0 rounded ${prevDone ? "bg-emerald-400" : "bg-zinc-200"}`} />}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function FederalProvenanceBadge({ deal, reload }) {
  // Cockpit-only (dir 64) — NEVER rendered into the federal PDF. Reads flags off the deal.
  const { user } = useAuth();
  const [busy, setBusy] = React.useState(false);
  const isOfficer = !!user;  // Dir 65 — roles flattened: any authenticated employee has full access.
  const rerun = async () => {
    const n = deal.ai_degraded_stage;
    if (!n) return;
    setBusy(true);
    try {
      await api.post(`/bids/${deal.id}/stages/${n}/redraft`);
      // Dir 70 — runs in the background (a stage can take minutes); poll status so we never hit a proxy 502.
      const started = Date.now();
      const poll = async () => {
        try {
          const { data } = await api.get(`/bids/${deal.id}/redraft-status`);
          if (data.status === "done") { toast.success(`Stage ${n} re-run — degradation cleared.`); setBusy(false); reload?.(); return; }
          if (data.status === "error") { toast.error(data.error || "Re-run failed."); setBusy(false); return; }
        } catch { /* transient */ }
        if (Date.now() - started > 300000) { toast.message("Still re-drafting… check back shortly."); setBusy(false); reload?.(); return; }
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Re-run failed."); setBusy(false); }
  };
  const badges = [];
  const es = deal.engine_status;
  if (es === "error" || deal.engine_error) {
    badges.push({ k: "err", c: "bg-red-100 text-red-700 border-red-300",
      t: `Engine error${deal.current_engine_stage ? ` (Stage ${deal.current_engine_stage})` : ""}`,
      title: deal.engine_error || "The engine failed — see the activity timeline." });
  }
  if (deal.ai_degraded) {
    badges.push({ k: "deg", c: "bg-amber-100 text-amber-800 border-amber-300",
      t: `AI degraded${deal.ai_degraded_stage ? ` (Stage ${deal.ai_degraded_stage})` : ""} — re-run`,
      title: deal.ai_degraded_detail || "A stage's AI output was truncated/salvaged; the send gate is held RED until re-run." });
  }
  if (deal.pricing_fallback_used) {
    badges.push({ k: "pf", c: "bg-amber-100 text-amber-800 border-amber-300", t: "Pricing used fallback — not grounded",
      title: "One or more base lines were priced by the deterministic fallback cascade — the F4 total is not grounded and the gate stays RED until grounded." });
  }
  if (badges.length === 0) {
    if (!deal.proposal_draft) return null;
    badges.push({ k: "ok", c: "bg-emerald-100 text-emerald-700 border-emerald-300", t: "AI-drafted (Opus 4.8)",
      title: "Federal stages ran clean on the approved model (Claude Opus 4.8, WD's key). Operator-only — never printed on the client PDF." });
  }
  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap" data-testid="federal-provenance-badge">
      {badges.map((b) => (
        <span key={b.k} data-testid={`federal-provenance-${b.k}`} title={b.title}
          className={`text-[10px] px-2 py-0.5 rounded-sm font-semibold border ${b.c}`}>{b.t}</span>
      ))}
      {deal.ai_degraded && isOfficer && (
        <button onClick={rerun} disabled={busy} data-testid="federal-rerun-stage-btn"
          title={`Re-run Stage ${deal.ai_degraded_stage} (non-destructive — regenerates only this stage's deliverable). Clears the degradation on success.`}
          className="text-[10px] inline-flex items-center gap-1 rounded-sm border border-zinc-300 text-zinc-700 hover:bg-zinc-50 px-1.5 py-0.5 disabled:opacity-50">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          {busy ? "Re-running…" : `Re-run Stage ${deal.ai_degraded_stage}`}
        </button>
      )}
    </span>
  );
}

function CommercialDraftBadge({ dealId, reload }) {
  const { user } = useAuth();
  const [src, setSrc] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const isOfficer = !!user;  // Dir 65 — roles flattened: any authenticated employee has full access.
  React.useEffect(() => {
    let alive = true;
    let timer = null;
    const load = () => api.get(`/bids/${dealId}/commercial-sections`)
      .then((r) => {
        if (!alive) return;
        setSrc(r.data?.source || null);
        if (r.data?.drafting) { timer = setTimeout(load, 3000); }  // Dir 70 — first draft runs in background
      })
      .catch(() => {});
    load();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [dealId]);
  const redraft = async () => {
    setBusy(true);
    try {
      await api.post(`/bids/${dealId}/commercial-sections/redraft`);
      // Dir 70 — background draft; poll commercial-sections until drafting flips false.
      const started = Date.now();
      const poll = async () => {
        try {
          const { data } = await api.get(`/bids/${dealId}/commercial-sections`);
          if (!data?.drafting) {
            setSrc(data?.source || null);
            if (data?.source === "anvil" || data?.source === "bruh") toast.success("Re-drafted by Anvil — fallback cleared.");
            else toast.message("Anvil still couldn't run (thin RFP or API). Terms remain heuristic — review before sending.");
            setBusy(false); reload?.(); return;
          }
        } catch { /* transient */ }
        if (Date.now() - started > 180000) { setBusy(false); reload?.(); return; }
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Re-draft failed."); setBusy(false); }
  };
  if (!src) return null;
  const MAP = {
    anvil: { c: "bg-emerald-100 text-emerald-700 border-emerald-300", t: "Drafted by Anvil (Claude)" },
    bruh: { c: "bg-emerald-100 text-emerald-700 border-emerald-300", t: "Drafted by Anvil (Claude)" },
    human: { c: "bg-blue-100 text-blue-700 border-blue-300", t: "Edited by human" },
    heuristic: { c: "bg-amber-100 text-amber-800 border-amber-300", t: "Heuristic fallback — Anvil did not run" },
  };
  const m = MAP[src] || MAP.heuristic;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span data-testid="commercial-draft-source-badge"
        title={src === "heuristic" ? "Anvil (Claude) did not run for this bid (thin RFP or API error). Terms were auto-generated by the deterministic heuristic — review before sending. Logged to the activity timeline." : "Anvil-authored commercial terms (operator-only; never printed on the client PDF)."}
        className={`text-[10px] px-2 py-0.5 rounded-sm font-semibold border ${m.c}`}>
        {m.t}
      </span>
      {src === "heuristic" && isOfficer && (
        <button onClick={redraft} disabled={busy} data-testid="commercial-redraft-anvil-btn"
          title="Ask Anvil (Claude) to draft the bespoke commercial terms again for this bid."
          className="text-[10px] inline-flex items-center gap-1 rounded-sm border border-zinc-300 text-zinc-700 hover:bg-zinc-50 px-1.5 py-0.5 disabled:opacity-50">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          {busy ? "Re-drafting…" : "Re-draft with Anvil"}
        </button>
      )}
    </span>
  );
}

function ProposalPdf({ deal, reload }) {
  const [full, setFull] = useState(false);
  const [status, setStatus] = useState(null); // 'none'|'generating'|'ready'|'error'
  const [ready, setReady] = useState(false);
  const [bust, setBust] = useState(() => Date.now());
  const pollRef = React.useRef(null);
  const isCommercial = deal.bid_type === "Commercial";
  // Dir 165 — a federal PRICE-ONLY sealed bid (IFB / RFQ) legitimately has an EMPTY Stage-5 narrative (no
  // technical/management/past-performance volume — FAR 14). The Stage-5 DELIVERABLE for those is the assembled
  // priced package (cover + SF-1442 + priced Bid Schedule + forms), which the backend renders even with an
  // empty draft. Gating the tab on proposal_draft alone made a completed price-only bid read "not generated
  // yet". So a federal bid HAS a proposal once Stage 5 is COMPLETE, regardless of the narrative length.
  const stage5Done = (deal.bid_stages || []).some((s) => String(s.number) === "5" && s.status === "COMPLETE");
  const [editing, setEditing] = useState(false);
  const [draftText, setDraftText] = useState("");
  const [savingDraft, setSavingDraft] = useState(false);
  const [reformatting, setReformatting] = useState(false);
  // Dir 178 — a legacy monolithic-blob draft (newlines were collapsed upstream): long text with almost no
  // line breaks. Offer a one-click deterministic re-format (no engine re-run) to restore its structure.
  const draftIsBlob = !isCommercial && (deal.proposal_draft || "").length > 1500
    && ((deal.proposal_draft || "").match(/\n/g) || []).length < 5;
  const [downloadingPkg, setDownloadingPkg] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiInstruction, setAiInstruction] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [editingComm, setEditingComm] = useState(false);
  const [commDraft, setCommDraft] = useState(null);
  const [savingComm, setSavingComm] = useState(false);
  const [rebuildTick, setRebuildTick] = useState(0);
  const kickRebuild = () => { setReady(false); setBust(Date.now()); setRebuildTick((t) => t + 1); };
  const [history, setHistory] = useState([]);
  const [histOpen, setHistOpen] = useState(false);
  const [revertingId, setRevertingId] = useState(null);
  const loadHistory = React.useCallback(async () => {
    try { const { data } = await api.get(`/bids/${deal.id}/proposal/ai-edits`); setHistory(data.edits || []); }
    catch { /* history strip is non-blocking */ }
  }, [deal.id]);
  const revertEdit = async (id) => {
    if (!window.confirm("Revert the proposal to its state before this edit? Any later AI edits will also be undone.")) return;
    setRevertingId(id);
    try {
      const { data } = await api.post(`/bids/${deal.id}/proposal/ai-edits/${id}/revert`);
      setHistory(data.edits || []);
      toast.success("Reverted. The proposal PDF is rebuilding.");
      kickRebuild(); reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Revert failed."); }
    finally { setRevertingId(null); }
  };

  // Cold-start-safe submission-package download: the bundle is background-generated + cached (never a
  // synchronous ~14s request that can 502). Poll /submission-status until fresh, then pull the blob and
  // trigger the browser download — the button shows a spinner the whole time (progress, never a dead request).
  const downloadSubmissionPackage = async () => {
    if (downloadingPkg) return;
    setDownloadingPkg(true);
    try {
      const started = Date.now();
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { data } = await api.get(`/bids/${deal.id}/submission-status`);
        if (data?.fresh) break;
        if (data?.blocked || data?.status === "error") {
          toast.error(data?.error ? `Submission package blocked: ${String(data.error).slice(0, 180)}`
                                  : "Submission package is blocked — resolve the flagged item, then retry.");
          setDownloadingPkg(false);
          return;
        }
        if (Date.now() - started > 180000) { toast.error("Submission package is taking too long — please retry."); setDownloadingPkg(false); return; }
        await new Promise((r) => setTimeout(r, 2500));
      }
      const resp = await api.get(`/bids/${deal.id}/submission-package.pdf`, { responseType: "blob" });
      if (resp.data?.type && resp.data.type.includes("json")) {
        toast.error("Submission package isn't ready yet — please retry in a moment.");
        setDownloadingPkg(false); return;
      }
      const sol = deal.solicitation_number || deal.commercial_proposal_number || deal.est_code || "bid";
      const blobUrl = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = blobUrl; a.download = `Submission_${sol}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Submission package downloaded.");
    } catch (e) {
      const d = e?.response?.data;
      toast.error(d?.reason || d?.detail || "Couldn't build the submission package. Please retry.");
    } finally {
      setDownloadingPkg(false);
    }
  };

  const saveDraft = async () => {
    if (!draftText.trim()) { toast.error("The proposal narrative can't be empty."); return; }
    setSavingDraft(true);
    try {
      const { data } = await api.put(`/bids/${deal.id}/proposal-draft`, { proposal_draft: draftText });
      toast.success(`Draft saved (${data.delta >= 0 ? "+" : ""}${data.delta} chars). PDF is rebuilding.${data.compliance_stale ? " Compliance flagged for re-check." : ""}`);
      setEditing(false); setReady(false); setBust(Date.now());
      reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setSavingDraft(false); }
  };

  // Dir 178 — one-click re-format of a legacy blob draft: deterministically reflows structure & rebuilds the
  // PDF. No LLM, no re-price, no re-draft.
  const reformatDraft = async () => {
    setReformatting(true);
    try {
      const { data } = await api.post(`/bids/${deal.id}/proposal/reformat`);
      if (data.changed) {
        toast.success("Proposal re-formatted — headings & paragraphs restored. The PDF is rebuilding.");
        kickRebuild(); reload?.();
      } else {
        toast.info("This proposal is already well-formatted — nothing to reflow.");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't re-format the proposal."); }
    finally { setReformatting(false); }
  };

  // AI-assisted edit (federal narrative OR commercial sections) — Anvil applies a plain-language instruction
  // while preserving structure; the PDF re-renders. Backend refuses to fabricate facts (502 → rephrase).
  const applyAiEdit = async () => {
    const ins = aiInstruction.trim();
    if (!ins) { toast.error("Describe the change you want Anvil to make."); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post(`/bids/${deal.id}/proposal/ai-edit`, { instruction: ins });
      toast.success((data.summary || "Anvil applied your edit.") + (data.compliance_stale ? " Compliance flagged for re-check." : " PDF is rebuilding."));
      setAiInstruction(""); setAiOpen(false);
      kickRebuild();
      loadHistory();
      reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Anvil couldn't apply that edit — try rephrasing it."); }
    finally { setAiBusy(false); }
  };

  // Manual commercial-terms editor — overview fields + bullet lists (one per line) → PUT /commercial-sections.
  const openCommEditor = async () => {
    try {
      const { data } = await api.get(`/bids/${deal.id}/commercial-sections`);
      const ov = data.overview || {};
      setCommDraft({
        overview: {
          description: ov.description || "", general_scope: ov.general_scope || "",
          objective: ov.objective || "", reference: ov.reference || "",
        },
        exclusions: (data.exclusions || []).join("\n"),
        assumptions: (data.assumptions || []).join("\n"),
        schedule: (data.schedule || []).join("\n"),
      });
      setAiOpen(false); setEditingComm(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load the commercial terms."); }
  };

  const saveCommEditor = async () => {
    if (!commDraft) return;
    const toList = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
    setSavingComm(true);
    try {
      await api.put(`/bids/${deal.id}/commercial-sections`, {
        overview: commDraft.overview,
        exclusions: toList(commDraft.exclusions),
        assumptions: toList(commDraft.assumptions),
        schedule: toList(commDraft.schedule),
      });
      toast.success("Commercial terms saved. PDF is rebuilding.");
      setEditingComm(false); kickRebuild(); reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setSavingComm(false); }
  };

  const poll = React.useCallback(async () => {
    try {
      const r = await api.get(`/bids/${deal.id}/proposal-status`);
      const st = r.data || {};
      setStatus(st.status);
      if (st.fresh) {
        setReady(true);
        setBust(Date.now());
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      } else {
        setReady(false);
      }
    } catch (e) { /* keep polling */ }
  }, [deal.id]);

  React.useEffect(() => {
    const hasProposal = isCommercial ? (deal.engine_status === "complete" || !!deal.commercial_sections) : (!!deal.proposal_draft || stage5Done);
    if (!hasProposal) return;
    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.id, deal.proposal_draft, deal.engine_status, isCommercial, stage5Done, rebuildTick]);

  React.useEffect(() => { loadHistory(); }, [loadHistory]);

  const hasProposal = isCommercial ? (deal.engine_status === "complete" || !!deal.commercial_sections) : (!!deal.proposal_draft || stage5Done);
  if (!hasProposal) return <Placeholder stage="5" what="Proposal" />;

  const url = `${API_BASE}/bids/${deal.id}/proposal.pdf?v=${bust}`;
  const viewer = `${url}#view=FitH&toolbar=1`;

  if (!ready) {
    return (
      <div className="flex flex-col items-center justify-center h-[420px] text-center" data-testid="proposal-generating">
        {status === "error" ? (
          <>
            <AlertTriangle className="h-7 w-7 text-red-500 mb-3" />
            <div className="text-sm font-medium text-zinc-900">Couldn't build the proposal PDF</div>
            <button onClick={poll} className="mt-3 flex items-center gap-1.5 text-xs bg-zinc-900 text-white px-3 py-1.5 rounded-md hover:bg-zinc-800" data-testid="proposal-retry"><RefreshCw className="h-3.5 w-3.5" /> Retry</button>
          </>
        ) : (
          <>
            <Loader2 className="h-7 w-7 text-gold-dark animate-spin mb-3" />
            <div className="text-sm font-medium text-zinc-900">Assembling the combined proposal PDF…</div>
            <div className="text-xs text-zinc-500 mt-1">Body + pricing + compliance + forms. This runs in the background so it won't time out.</div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="proposal-pdf">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-xs text-zinc-900 font-mono">{isCommercial ? "WD commercial template · Project Proposal" : "WD federal template · proposal + forms combined into one PDF"}</div>
          {isCommercial && <CommercialDraftBadge dealId={deal.id} reload={reload} />}
          {!isCommercial && <FederalProvenanceBadge deal={deal} reload={reload} />}
        </div>
        <div className="flex items-center gap-2">
          {!isCommercial && draftIsBlob && (
            <button onClick={reformatDraft} disabled={reformatting} data-testid="proposal-reformat-btn"
              title="This proposal's paragraph/heading structure was flattened. Re-format restores it instantly — no re-draft, no re-price."
              className="flex items-center gap-1 text-[11px] bg-amber-100 text-amber-800 border border-amber-300 px-2 py-1 rounded-md hover:bg-amber-200 disabled:opacity-60 disabled:cursor-wait">
              {reformatting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <AlignLeft className="h-3.5 w-3.5" />}
              {reformatting ? "Re-formatting…" : "Re-format proposal"}</button>
          )}
          {!isCommercial && (
            <button onClick={() => { setDraftText(deal.proposal_draft || ""); setEditing(true); }} data-testid="proposal-edit-draft-btn"
              className="flex items-center gap-1 text-[11px] text-zinc-600 hover:text-zinc-900"><PenLine className="h-3.5 w-3.5" /> Edit draft</button>
          )}
          {isCommercial && (
            <button onClick={openCommEditor} data-testid="commercial-edit-terms-btn"
              className="flex items-center gap-1 text-[11px] text-zinc-600 hover:text-zinc-900"><PenLine className="h-3.5 w-3.5" /> Edit terms</button>
          )}
          <button onClick={() => { setAiOpen((v) => !v); setEditing(false); setEditingComm(false); }} data-testid="proposal-ai-edit-toggle"
            className={`flex items-center gap-1 text-[11px] ${aiOpen ? "text-gold-dark font-semibold" : "text-zinc-600 hover:text-zinc-900"}`}><Sparkles className="h-3.5 w-3.5" /> Ask Anvil to edit</button>
          <button onClick={() => { const n = !histOpen; setHistOpen(n); if (n) loadHistory(); }} data-testid="proposal-history-toggle"
            className={`flex items-center gap-1 text-[11px] ${histOpen ? "text-gold-dark font-semibold" : "text-zinc-600 hover:text-zinc-900"}`}><History className="h-3.5 w-3.5" /> History{history.length ? ` (${history.length})` : ""}</button>
          <button onClick={() => setFull(true)} className="flex items-center gap-1 text-[11px] text-zinc-600 hover:text-zinc-900" data-testid="proposal-expand"><Maximize2 className="h-3.5 w-3.5" /> Fullscreen</button>
          <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[11px] text-zinc-600 hover:text-zinc-900" data-testid="proposal-open"><ExternalLink className="h-3.5 w-3.5" /> Open preview</a>
          <button onClick={downloadSubmissionPackage} disabled={downloadingPkg} data-testid="submission-package-download"
             title="The single deliverable: one ordered CO-facing package (cover, technical, priced schedule, filled gov forms, reps/certs). Anything missing shows on the platform, never in the file. Sendability is governed by the send-readiness gate (signatures, certs, forms)."
             className="flex items-center gap-1 text-[11px] bg-gold-dark text-white px-2.5 py-1 rounded-md hover:opacity-90 disabled:opacity-60 disabled:cursor-wait">
            {downloadingPkg ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileCheck2 className="h-3.5 w-3.5" />}
            {downloadingPkg ? "Assembling…" : "Download Submission Package"}</button>
        </div>
      </div>
      {!isCommercial && !deal.proposal_draft && stage5Done && (
        <div className="mb-2 text-[11px] text-zinc-500 bg-indigo-50 border border-indigo-100 rounded-md px-2.5 py-1.5" data-testid="proposal-price-only-note">
          Price-only sealed bid (IFB / FAR 14) — no technical/management narrative volume is required. The deliverable is the assembled priced package shown below (cover · SF-1442 · priced Bid Schedule · forms). Use <b>Edit draft</b> or <b>Ask Anvil to edit</b> to add an optional cover narrative.
        </div>
      )}
      {editing ? (
        <div className="flex flex-col flex-1 min-h-0" data-testid="proposal-draft-editor">
          <div className="text-[11px] text-zinc-500 mb-1.5">
            Editing the <b>Stage-5 narrative</b> only (markdown — keep <span className="font-mono">#</span>/<span className="font-mono">##</span> headings). Saving rebuilds the PDF and, if compliance was already built, flags it for re-check. Pricing, forms, reps/certs &amp; signatures are untouched.
          </div>
          <textarea value={draftText} onChange={(e) => setDraftText(e.target.value)} data-testid="proposal-draft-textarea"
            className="flex-1 w-full min-h-[60vh] font-mono text-[12px] leading-relaxed border border-zinc-300 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-gold-dark" />
          <div className="flex items-center gap-2 mt-2">
            <button onClick={saveDraft} disabled={savingDraft} data-testid="proposal-draft-save"
              className="flex items-center gap-1 text-[11px] bg-gold-dark text-white px-3 py-1.5 rounded-md hover:opacity-90 disabled:opacity-50">
              {savingDraft ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileCheck2 className="h-3.5 w-3.5" />} {savingDraft ? "Saving…" : "Save draft"}</button>
            <button onClick={() => setEditing(false)} data-testid="proposal-draft-cancel"
              className="text-[11px] text-zinc-500 hover:text-zinc-800 px-2 py-1.5">Cancel</button>
            <span className="text-[10px] text-zinc-400 ml-auto">{draftText.length.toLocaleString()} chars</span>
          </div>
        </div>
      ) : editingComm && commDraft ? (
        <div className="flex flex-col flex-1 min-h-0 overflow-y-auto" data-testid="commercial-terms-editor">
          <div className="text-[11px] text-zinc-500 mb-2">
            Editing the <b>commercial proposal terms</b>. One bullet per line for Exclusions / Assumptions / Schedule. Saving rebuilds the PDF. Pricing &amp; forms are untouched.
          </div>
          <div className="space-y-2">
            {[["description", "Overview — description"], ["general_scope", "General scope"], ["objective", "Objective"], ["reference", "Reference"]].map(([k, label]) => (
              <div key={k}>
                <label className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">{label}</label>
                <textarea value={commDraft.overview[k]} onChange={(e) => setCommDraft((d) => ({ ...d, overview: { ...d.overview, [k]: e.target.value } }))}
                  data-testid={`comm-overview-${k}`} rows={k === "description" ? 3 : 2}
                  className="w-full text-[12px] border border-zinc-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-gold-dark" />
              </div>
            ))}
            {[["exclusions", "Exclusions (one per line)"], ["assumptions", "Assumptions (one per line)"], ["schedule", "Schedule (one per line)"]].map(([k, label]) => (
              <div key={k}>
                <label className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">{label}</label>
                <textarea value={commDraft[k]} onChange={(e) => setCommDraft((d) => ({ ...d, [k]: e.target.value }))}
                  data-testid={`comm-${k}`} rows={4}
                  className="w-full text-[12px] font-mono border border-zinc-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-gold-dark" />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-3">
            <button onClick={saveCommEditor} disabled={savingComm} data-testid="commercial-terms-save"
              className="flex items-center gap-1 text-[11px] bg-gold-dark text-white px-3 py-1.5 rounded-md hover:opacity-90 disabled:opacity-50">
              {savingComm ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileCheck2 className="h-3.5 w-3.5" />} {savingComm ? "Saving…" : "Save terms"}</button>
            <button onClick={() => setEditingComm(false)} data-testid="commercial-terms-cancel"
              className="text-[11px] text-zinc-500 hover:text-zinc-800 px-2 py-1.5">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col flex-1 min-h-0">
          {aiOpen && (
            <div className="mb-2 border border-gold-dark/40 bg-amber-50/50 rounded-md p-3" data-testid="proposal-ai-edit-panel">
              <div className="text-[11px] text-zinc-600 mb-1.5 flex items-start gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-gold-dark shrink-0 mt-0.5" />
                <span>Describe the change in plain language — Anvil edits {isCommercial ? "the commercial terms" : "the narrative"} while preserving structure. It never invents prices, dates, or facts.</span>
              </div>
              <textarea value={aiInstruction} onChange={(e) => setAiInstruction(e.target.value)} data-testid="proposal-ai-edit-input"
                rows={3} placeholder="e.g. Add a short paragraph on our 24/7 emergency response to the overview."
                className="w-full text-[12px] border border-zinc-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-gold-dark" />
              <div className="flex items-center gap-2 mt-2">
                <button onClick={applyAiEdit} disabled={aiBusy || !aiInstruction.trim()} data-testid="proposal-ai-edit-apply"
                  className="flex items-center gap-1 text-[11px] bg-gold-dark text-white px-3 py-1.5 rounded-md hover:opacity-90 disabled:opacity-50">
                  {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {aiBusy ? "Applying…" : "Apply edit"}</button>
                <button onClick={() => { setAiOpen(false); setAiInstruction(""); }} data-testid="proposal-ai-edit-cancel"
                  className="text-[11px] text-zinc-500 hover:text-zinc-800 px-2 py-1.5">Cancel</button>
              </div>
            </div>
          )}
          {histOpen && (
            <div className="mb-2 border border-zinc-200 bg-white rounded-md p-3 max-h-60 overflow-y-auto" data-testid="proposal-history-panel">
              <div className="text-[11px] font-semibold text-zinc-700 mb-2 flex items-center gap-1.5">
                <History className="h-3.5 w-3.5 text-zinc-500" /> AI edit history — reverting restores the proposal to its state <b>before</b> that edit (later edits are undone too)
              </div>
              {history.length === 0 ? (
                <div className="text-[11px] text-zinc-400 py-2" data-testid="proposal-history-empty">No Anvil edits yet — use “Ask Anvil to edit” to make one.</div>
              ) : (
                <ul className="space-y-1.5">
                  {history.map((h, i) => (
                    <li key={h.id} data-testid={`proposal-history-entry-${i}`}
                      className={`flex items-start gap-2 border rounded-md px-2.5 py-1.5 ${h.reverted ? "border-zinc-200 bg-zinc-50 opacity-70" : "border-zinc-200"}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-[12px] text-zinc-800 break-words">{h.instruction}</div>
                        <div className="text-[10px] text-zinc-500 mt-0.5">
                          {h.actor || "—"} · {h.at ? new Date(h.at).toLocaleString() : ""}
                          {typeof h.chars_before === "number" && typeof h.chars_after === "number"
                            ? ` · ${h.chars_after - h.chars_before >= 0 ? "+" : ""}${h.chars_after - h.chars_before} chars` : ""}
                        </div>
                      </div>
                      {h.reverted ? (
                        <span className="shrink-0 text-[10px] font-medium text-zinc-400" data-testid={`proposal-history-reverted-${i}`}>reverted</span>
                      ) : (
                        <button onClick={() => revertEdit(h.id)} disabled={revertingId === h.id} data-testid={`proposal-history-revert-${i}`}
                          className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold text-red-700 hover:underline disabled:opacity-50">
                          {revertingId === h.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />} Revert
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <iframe title="WD Proposal PDF" src={viewer} className="flex-1 w-full min-h-[68vh] border border-zinc-200 rounded-md bg-zinc-50" data-testid="proposal-iframe" />
        </div>
      )}
      {full && (
        <div className="fixed inset-0 z-[60] bg-zinc-900/90 backdrop-blur-sm flex flex-col p-3" data-testid="proposal-fullscreen" onClick={() => setFull(false)}>
          <div className="flex items-center justify-between text-white mb-2 px-1" onClick={(e) => e.stopPropagation()}>
            <span className="text-sm font-medium">{deal.title} · Proposal</span>
            <div className="flex items-center gap-3">
              <button onClick={downloadSubmissionPackage} disabled={downloadingPkg} data-testid="submission-package-download-fs"
                className="flex items-center gap-1 text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-md disabled:opacity-60 disabled:cursor-wait">
                {downloadingPkg ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {downloadingPkg ? "Assembling…" : "Submission Package"}</button>
              <button onClick={() => setFull(false)} className="flex items-center gap-1 text-xs hover:text-zinc-300" data-testid="proposal-fullscreen-close"><X className="h-5 w-5" /></button>
            </div>
          </div>
          <iframe title="WD Proposal PDF Fullscreen" src={viewer} className="flex-1 w-full rounded-md bg-white" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

function Compliance({ deal, reload }) {
  const [data, setData] = React.useState(null);
  const [respOnly, setRespOnly] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [res, setRes] = React.useState(null);
  const [rechecking, setRechecking] = React.useState(false);
  const initRef = React.useRef(false);
  const loadComp = React.useCallback(() => {
    setData(null);
    api.get(`/bids/${deal.id}/compliance`).then((r) => setData(r.data)).catch(() => setData({ compliance: [] }));
  }, [deal.id]);
  React.useEffect(() => { loadComp(); }, [loadComp]);

  const rawRows = data?.compliance || [];
  const allRows = rawRows.filter((r) => !r.non_requirement);
  const boilerCount = rawRows.length - allRows.length;
  const isIFB = (deal.solicitation_type || "") === "IFB";
  const scopedDefault = ((data?.effective_scope) || (isIFB ? "responsiveness" : "full")) === "responsiveness";
  React.useEffect(() => {
    if (data && !initRef.current) { initRef.current = true; setRespOnly(scopedDefault); }
  }, [data, scopedDefault]);
  const isGating = (r) => (r.obligation_type || "Responsiveness") === "Responsiveness";
  const isGap = (s) => { const x = (s || "").toLowerCase(); return x.includes("gap") || x.includes("missing") || x.includes("partial") || x.includes("not"); };
  const gatingGaps = allRows.filter((r) => isGap(r.status) && (scopedDefault ? isGating(r) : true));
  const rows = respOnly ? allRows.filter(isGating) : allRows;
  const shownGaps = rows.filter((r) => isGap(r.status));
  const green = rows.length - shownGaps.length;
  const [visCount, sentinelRef] = useIncremental(rows.length, 40);

  const resolve = async () => {
    setBusy(true); setRes(null);
    try {
      await api.post(`/bids/${deal.id}/resolve-compliance-gaps`);
      const t0 = Date.now();
      const poll = async () => {
        const { data: gr } = await api.get(`/bids/${deal.id}/gap-resolution`);
        if (gr.status === "done") { setRes(gr); setBusy(false); toast.success(`${gr.resolved} covered (cited) · ${gr.still_missing} genuinely missing`); loadComp(); reload?.(); return; }
        if (gr.status === "error") { setBusy(false); toast.error("Gap resolution failed."); return; }
        if (Date.now() - t0 > 180000) { setBusy(false); toast.error("Gap resolution timed out."); return; }
        setTimeout(poll, 2500);
      };
      poll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gap resolution failed."); setBusy(false); }
  };

  const recheckCompliance = async () => {
    setRechecking(true);
    try {
      await api.post(`/bids/${deal.id}/stages/6/redraft`);
      const t0 = Date.now();
      const poll = async () => {
        try {
          const { data: rs } = await api.get(`/bids/${deal.id}/redraft-status`);
          if (rs.status === "done") { toast.success("Compliance re-checked against the edited narrative."); setRechecking(false); loadComp(); reload?.(); return; }
          if (rs.status === "error") { toast.error(rs.error || "Re-check failed."); setRechecking(false); return; }
        } catch { /* transient */ }
        if (Date.now() - t0 > 300000) { toast.message("Still re-checking… check back shortly."); setRechecking(false); reload?.(); return; }
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Re-check failed."); setRechecking(false); }
  };

  const [enforcing, setEnforcing] = React.useState(false);
  const enforceCitations = async () => {
    setEnforcing(true);
    try {
      const { data: er } = await api.post(`/bids/${deal.id}/enforce-compliance-citations?use_llm=false`);
      toast.success(`${er.verified} cited & verified · ${er.flagged} unverified (flagged) · ${er.informational} informational`);
      loadComp(); reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Citation enforcement failed."); }
    setEnforcing(false);
  };

  if (!data) return <div className="flex items-center justify-center h-[180px] text-sm text-zinc-400" data-testid="compliance-loading"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading compliance…</div>;
  if (!rawRows.length) return <Placeholder stage="6" what="Compliance checklist" />;
  const shownRows = rows.slice(0, visCount);

  return (
    <div data-testid="compliance-checklist">
      {deal.compliance_stale && (
        <div className="mb-3 border border-amber-300 bg-amber-50 rounded-md px-3 py-2 flex items-center justify-between gap-2" data-testid="compliance-stale-banner">
          <span className="text-[12px] text-amber-800 flex items-center gap-1.5">
            <AlertTriangle className="h-4 w-4 shrink-0" /> The proposal narrative was edited after this matrix was built — it may be stale. Re-check before sending.
          </span>
          <button onClick={recheckCompliance} disabled={rechecking} data-testid="compliance-recheck-btn"
            className="shrink-0 text-[11px] bg-amber-700 text-white px-3 py-1.5 rounded-md hover:bg-amber-800 disabled:opacity-50 flex items-center gap-1">
            {rechecking ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} {rechecking ? "Re-checking…" : "Re-check compliance"}</button>
        </div>
      )}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-[11px]">
          <span data-testid="solicitation-type-badge" className={`px-2 py-0.5 rounded-sm font-semibold ${isIFB ? "bg-indigo-100 text-indigo-700" : "bg-violet-100 text-violet-700"}`}>
            {deal.solicitation_type || "Unknown"} {isIFB ? "· sealed bid" : deal.solicitation_type === "RFP" ? "· negotiated" : ""}
          </span>
          <span className="text-zinc-500">Gate: {scopedDefault ? "Responsiveness only (FAR 14)" : "Full Section L/M"}</span>
        </div>
        <button data-testid="resp-view-toggle" onClick={() => setRespOnly((v) => !v)}
          className="text-[11px] flex items-center gap-1 text-zinc-600 hover:text-zinc-900 border border-zinc-200 rounded-md px-2 py-1">
          {respOnly ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
          {respOnly ? "Responsiveness only" : "All requirements"}
        </button>
      </div>
      <div className={`flex items-center justify-between gap-3 mb-3 px-3 py-2 rounded-md text-sm ${gatingGaps.length ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
        <span className="font-medium flex items-center gap-2">
          {gatingGaps.length ? <AlertTriangle className="h-4 w-4" /> : <FileCheck2 className="h-4 w-4" />}
          {gatingGaps.length
            ? `${gatingGaps.length} ${scopedDefault ? "responsiveness " : ""}item(s) MISSING / GAP — submission disabled until green`
            : `All ${scopedDefault ? "responsiveness " : ""}items green — cleared to submit`}
        </span>
        <span className="text-xs font-mono whitespace-nowrap">{green}/{rows.length} shown</span>
      </div>
      {boilerCount > 0 && (
        <div className="mb-3 -mt-1 text-[11px] text-zinc-400 flex items-center gap-1.5" data-testid="compliance-boilerplate-note">
          <EyeOff className="h-3 w-3" /> {boilerCount} non-requirement line{boilerCount === 1 ? "" : "s"} (SF-30 amendment headers, form/version stamps, page markers) hidden — not counted as gaps.
        </div>
      )}
      {gatingGaps.length > 0 && (
        <div className="mb-3 flex items-center justify-between gap-2 border border-zinc-200 rounded-md px-3 py-2 bg-zinc-50">
          <div className="text-[11px] text-zinc-600">Anvil auto-closes only gaps that are GENUINELY covered (real citation). Truly missing items stay flagged — never gamed.</div>
          <button onClick={resolve} disabled={busy} data-testid="resolve-gaps-btn"
            className="shrink-0 text-[11px] bg-zinc-900 text-white px-3 py-1.5 rounded-md hover:bg-zinc-800 disabled:opacity-50 flex items-center gap-1">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileCheck2 className="h-3 w-3" />} {busy ? "Resolving…" : "Resolve compliance gaps"}</button>
        </div>
      )}
      <div className="mb-3 flex items-center justify-between gap-2 border border-zinc-200 rounded-md px-3 py-2 bg-zinc-50">
        <div className="text-[11px] text-zinc-600">Every <b>Covered/Partial</b> row must point at a real location (outline section, filled form, active SAM, or FAR/DFARS clause). Enforcement flags any that can't be substantiated.</div>
        <button onClick={enforceCitations} disabled={enforcing} data-testid="enforce-citations-btn"
          className="shrink-0 text-[11px] bg-indigo-700 text-white px-3 py-1.5 rounded-md hover:bg-indigo-800 disabled:opacity-50 flex items-center gap-1">
          {enforcing ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileCheck2 className="h-3 w-3" />} {enforcing ? "Checking…" : "Enforce citations"}</button>
      </div>
      {res && (
        <div className="mb-3 border border-blue-200 bg-blue-50 rounded-md px-3 py-2 text-[12px] text-blue-900" data-testid="resolve-gaps-result">
          Classified {res.classified} · <b>{res.resolved} covered</b> (cited & validated) · <b>{res.still_missing} genuinely missing</b> (stay flagged, not auto-closed).
        </div>
      )}
      {!respOnly && isIFB && (
        <div className="mb-2 text-[11px] text-zinc-400">Performance Obligations are accepted by your bid and tracked, but don't block submission on a sealed bid (IFB).</div>
      )}
      <div className="space-y-1.5">
        {shownRows.map((r, i) => {
          const gap = isGap(r.status);
          const perf = (r.obligation_type || "Responsiveness") !== "Responsiveness";
          return (
            <div key={i} data-testid={`compliance-row-${i}`} className={`flex items-start gap-2.5 border rounded-md px-3 py-2 ${gap && !perf ? "border-red-200 bg-red-50/40" : perf && gap ? "border-zinc-200 bg-zinc-50" : "border-emerald-100"}`}>
              {gap ? (perf ? <Circle className="h-4 w-4 text-zinc-400 shrink-0 mt-0.5" /> : <AlertTriangle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />) : <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm text-zinc-800">{r.requirement}</div>
                {r.where && <div className="text-[11px] text-zinc-500 mt-0.5">↳ {r.where}</div>}
                {r.citation_status === "verified" && (
                  <div className="text-[10px] text-emerald-600 mt-0.5 flex items-center gap-1" data-testid={`citation-verified-${i}`}>
                    <CheckCircle2 className="h-3 w-3" /> Citation verified
                  </div>
                )}
                {r.citation_status === "unverified" && (
                  <div className="text-[10px] text-amber-700 mt-0.5 flex items-center gap-1" data-testid={`citation-unverified-${i}`}>
                    <AlertTriangle className="h-3 w-3 shrink-0" /> {r.citation_flag || "No verifiable citation — substantiate or reclassify."}
                  </div>
                )}
                {perf && <div className="text-[10px] text-zinc-400 mt-0.5">Performance Obligation — accepted by bid (non-gating)</div>}
              </div>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-sm shrink-0 ${gap ? (perf ? "bg-zinc-200 text-zinc-600" : "bg-red-100 text-red-700") : "bg-emerald-100 text-emerald-700"}`}>{r.status}</span>
            </div>
          );
        })}
        {visCount < rows.length && <div ref={sentinelRef} data-testid="compliance-more" className="py-3 text-center text-[11px] text-zinc-400">Loading more… ({visCount}/{rows.length})</div>}
      </div>
    </div>
  );
}

const FORM_BADGE = {
  Filled: "bg-emerald-100 text-emerald-700", Covered: "bg-emerald-100 text-emerald-700",
  Attested: "bg-blue-100 text-blue-700", "Action required": "bg-amber-100 text-amber-700",
  "Needs review": "bg-amber-100 text-amber-700", Manual: "bg-amber-100 text-amber-700",
  Missing: "bg-red-100 text-red-700", Error: "bg-red-100 text-red-700", "Not Started": "bg-zinc-100 text-zinc-500",
};

const CANON_KEYS = [
  ["legal_name", "Legal name"], ["uei", "UEI"], ["cage", "CAGE code"], ["tin", "TIN / EIN"],
  ["address", "Address"], ["phone", "Phone"], ["email", "Email"], ["signer_name", "Signer name"],
  ["signer_title", "Signer title"], ["amount", "Bid amount"], ["solicitation", "Solicitation no."],
  ["title", "Project title"], ["date", "Date (e-sign)"], ["signature", "Signature (e-sign)"],
];

function AmendmentsPanel({ deal, reload }) {
  const [amd, setAmd] = React.useState(deal.amendments || null);
  const [busy, setBusy] = React.useState(false);
  const [attesting, setAttesting] = React.useState(null);
  const [note, setNote] = React.useState("");
  const load = React.useCallback(async () => {
    try { const { data } = await api.get(`/bids/${deal.id}/amendments`); setAmd(data); } catch (e) {}
  }, [deal.id]);
  React.useEffect(() => { load(); }, [load]);
  const attest = async (num) => {
    try { const { data } = await api.post(`/bids/${deal.id}/amendments/${num}/attest`, { note: note || "Acknowledged on the offer form" }); setAmd(data); setAttesting(null); setNote(""); toast.success(`Amendment ${num} acknowledged.`); reload?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Attest failed."); }
  };
  const ackAll = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/bids/${deal.id}/amendments/acknowledge-all`); setAmd(data); toast.success("Receipt of all amendments acknowledged (SF-30)."); reload?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Acknowledge-all failed."); }
    setBusy(false);
  };
  const impactAddressed = async (num) => {
    try { const { data } = await api.post(`/bids/${deal.id}/amendments/${num}/impact-addressed`, { note: "Re-shred / re-price complete" }); setAmd(data); toast.success(`Amendment ${num} impact marked addressed.`); reload?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };
  const recheck = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/bids/${deal.id}/amendments/recheck`); toast.success(data.note || "SAM re-check done."); await load(); reload?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Re-check failed."); }
    setBusy(false);
  };
  // Dir 122 — deliberate officer attestation of a MANUAL SAM.gov re-check (clears the "AMENDMENT STATUS
  // UNKNOWN" dead-end when there is no source URL to auto re-pull). Never auto-clears.
  const [foundOpen, setFoundOpen] = React.useState(false);
  const [foundNums, setFoundNums] = React.useState("");
  const attestRecheck = async (result, amendmentsList) => {
    setBusy(true);
    try {
      const body = { result, note: result === "none" ? "Officer manually verified SAM.gov — no new amendments" : "" };
      if (result === "found") body.amendments = amendmentsList || [];
      const { data } = await api.post(`/bids/${deal.id}/amendments/attest-recheck`, body);
      setAmd(data); setFoundOpen(false); setFoundNums("");
      toast.success(result === "none" ? "Amendment status confirmed — send gate cleared." : "Amendment(s) recorded — acknowledge before sending.");
      reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Attestation failed."); }
    setBusy(false);
  };
  const submitFound = () => {
    const list = (foundNums || "").split(",").map((s) => s.trim()).filter(Boolean).map((n) => ({ number: n }));
    if (!list.length) { toast.error("Enter at least one amendment number (e.g. 0001, 0002)."); return; }
    attestRecheck("found", list);
  };
  // Render whenever there are amendments OR the set is UNVERIFIED (the dead-end case has count===0).
  // When verified with zero amendments, show a compact confirmation pill (paper trail) instead of nothing.
  if (!amd) return null;
  const rc = amd.sam_recheck || {};
  if (!amd.count && amd.amendment_set_verified !== false) {
    if (!rc.checked) return null;
    return (
      <div className="mb-4 border border-emerald-200 bg-emerald-50/50 rounded-lg px-3 py-2 flex items-center gap-2 flex-wrap" data-testid="amendments-verified-pill">
        <FileCheck2 className="h-3.5 w-3.5 text-emerald-700" />
        <span className="text-[11px] text-emerald-800 font-medium">
          Amendment set verified{rc.method === "manual" ? " (officer-attested)" : " (auto re-check)"}
          {rc.attested_by ? ` by ${rc.attested_by}` : ""}{rc.at ? ` · ${new Date(rc.at).toLocaleDateString()}` : ""} — no open amendments.
        </span>
        <button onClick={recheck} disabled={busy} data-testid="amendments-reverify-btn"
          className="text-[11px] flex items-center gap-1 text-zinc-500 hover:text-zinc-800 ml-auto">
          <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} /> Re-check</button>
      </div>
    );
  }
  const verified = amd.amendment_set_verified !== false;
  const open = (amd.amendments || []).filter((a) => !a.acknowledged).length;
  const impactsOpen = (amd.amendments || []).filter((a) => a.impact?.material && !a.impact?.addressed).length;
  const locked = open || impactsOpen || !verified;
  return (
    <div className={`mb-4 border rounded-lg p-3 ${locked ? "border-red-300 bg-red-50" : "border-emerald-200 bg-emerald-50/40"}`} data-testid="amendments-panel">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="text-[11px] font-semibold uppercase tracking-wide flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5" /> Amendment acknowledgment {!verified ? "— STATUS UNKNOWN" : open ? `— ${open} unacknowledged` : impactsOpen ? `— ${impactsOpen} impact(s) open` : "— all cleared"} {locked ? "(send gate LOCKED)" : ""}
        </div>
        <div className="flex items-center gap-2">
          {open ? <button onClick={ackAll} disabled={busy} data-testid="amendments-ack-all-btn"
            className="text-[11px] flex items-center gap-1 bg-zinc-900 text-white px-2.5 py-1 rounded hover:bg-zinc-800 disabled:opacity-50">
            <FileCheck2 className="h-3 w-3" /> Acknowledge all via SF-30</button> : null}
          <button onClick={recheck} disabled={busy} data-testid="amendments-recheck-btn"
            className="text-[11px] flex items-center gap-1 text-zinc-600 hover:text-zinc-900">
            <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} /> SAM re-check</button>
        </div>
      </div>
      {!verified ? (
        <div className="mb-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2" data-testid="amendment-unknown-attest">
          <p className="text-[11px] text-amber-800">
            The automatic SAM.gov re-check could not verify the amendment set for this bid. Check the posting on
            SAM.gov for any last-minute amendments, then confirm below (deliberate officer action — never auto-cleared).
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button onClick={() => attestRecheck("none")} disabled={busy} data-testid="amendment-attest-recheck-none-btn"
              className="text-[11px] flex items-center gap-1 bg-emerald-700 text-white px-2.5 py-1.5 rounded hover:bg-emerald-800 disabled:opacity-50">
              <FileCheck2 className="h-3 w-3" /> I checked SAM.gov — no new amendments
            </button>
            <button onClick={() => setFoundOpen(!foundOpen)} disabled={busy} data-testid="amendment-attest-recheck-found-toggle"
              className="text-[11px] text-amber-800 underline hover:text-amber-900">I found amendment(s)</button>
          </div>
          {foundOpen ? (
            <div className="mt-2 flex items-center gap-2" data-testid="amendment-found-form">
              <input value={foundNums} onChange={(e) => setFoundNums(e.target.value)} placeholder="Amendment numbers e.g. 0001, 0002"
                data-testid="amendment-found-numbers" className="text-[12px] border border-zinc-300 rounded px-2 py-1 w-56" />
              <button onClick={submitFound} disabled={busy} data-testid="amendment-found-save-btn"
                className="text-[11px] bg-red-600 text-white px-2.5 py-1 rounded">Record for acknowledgment</button>
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="space-y-2">
        {(amd.amendments || []).map((a, i) => (
          <div key={i} data-testid={`amendment-row-${a.number}`} className="border-b border-zinc-100 pb-2">
            <div className="flex items-center justify-between gap-2 text-[12px]">
              <div className="min-w-0">
                <span className="font-mono font-semibold">Amendment {a.number}</span>
                {a.date ? <span className="text-zinc-500"> · {a.date}</span> : null}
                {a.impact?.material ? <span className="ml-2 text-[10px] uppercase font-semibold text-red-600 bg-red-100 px-1.5 py-0.5 rounded">material: {(a.impact.impacts || []).join("/")}</span> : null}
                <div className="text-[11px] text-zinc-500 truncate">{a.acknowledged ? (a.methods || []).join("; ") : "receipt NOT acknowledged — responsiveness DQ risk"}</div>
              </div>
              {a.acknowledged
                ? <span className="text-[11px] text-emerald-700 font-medium whitespace-nowrap">✓ receipt ack</span>
                : attesting === a.number
                  ? (<div className="flex items-center gap-1">
                      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="how acknowledged"
                        data-testid={`amendment-attest-note-${a.number}`} className="text-[12px] border border-zinc-300 rounded px-2 py-1 w-40" />
                      <button onClick={() => attest(a.number)} data-testid={`amendment-attest-save-${a.number}`} className="text-[11px] bg-zinc-900 text-white px-2 py-1 rounded">Ack</button>
                    </div>)
                  : <button onClick={() => setAttesting(a.number)} data-testid={`amendment-attest-btn-${a.number}`}
                      className="text-[11px] bg-red-600 text-white px-2 py-1 rounded whitespace-nowrap">Acknowledge</button>}
            </div>
            {a.impact?.summary ? <div className="mt-1 text-[11px] text-foreground bg-muted/60 rounded px-2 py-1"><span className="font-medium">What changed:</span> {a.impact.summary}</div> : null}
            {a.impact?.material && !a.impact?.addressed ? (
              <div className="mt-1 flex items-center justify-between gap-2 bg-red-100/70 rounded px-2 py-1">
                <span className="text-[11px] text-red-700">Material change — re-shred / re-price, then confirm. Receipt ack alone does not clear this.</span>
                <button onClick={() => impactAddressed(a.number)} data-testid={`amendment-impact-addressed-${a.number}`}
                  className="text-[11px] bg-red-600 text-white px-2 py-1 rounded whitespace-nowrap">Mark impact addressed</button>
              </div>
            ) : null}
          </div>
        ))}
      </div>
      {amd.proposal_ack_text ? <div className="mt-2 text-[11px] text-zinc-500 italic" data-testid="amendments-proposal-ref">Proposal reference: {amd.proposal_ack_text}</div> : null}
    </div>
  );
}

function Forms({ deal, reload }) {
  const [fs, setFs] = React.useState(deal.forms_status || null);
  const [busy, setBusy] = React.useState(false);
  const [attesting, setAttesting] = React.useState(null);
  const [note, setNote] = React.useState("");
  const [xfaForm, setXfaForm] = React.useState(null);
  React.useEffect(() => { setFs(deal.forms_status || null); }, [deal.forms_status]);

  const process = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/bids/${deal.id}/forms/process`); setFs(data); toast.success("Forms processed."); reload?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Form processing failed."); }
    setBusy(false);
  };
  const signAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bids/${deal.id}/forms/sign-all`);
      setFs(data.forms_status);
      toast.success(`Signed as ${data.signer} — SF-1442 20b/20c${data.other_fields_signed ? ` +${data.other_fields_signed} field(s)` : ""}. Signing does not send.`);
      reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sign failed."); }
    setBusy(false);
  };
  const unsignAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bids/${deal.id}/forms/unsign`);
      setFs(data.forms_status);
      toast.success("Signatures removed — forms are unsigned again. The send gate now requires a fresh Sign.");
      reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove signature failed."); }
    setBusy(false);
  };
  const attest = async (key) => {
    try { const { data } = await api.post(`/bids/${deal.id}/forms/${key}/attest`, { note: note || "Attested complete" }); setFs(data); setAttesting(null); setNote(""); toast.success("Attested."); reload?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Attest failed."); }
  };
  const mapCorrect = async (key, label, mapKey) => {
    try {
      const { data } = await api.post(`/bids/${deal.id}/forms/${key}/map-correction`, { label, key: mapKey });
      setFs(data.forms_status); toast.success(mapKey ? "Learned — this form will auto-fill that field next time" : "Mapping cleared"); reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Correction failed."); }
  };
  const reupload = async (key, fileEl) => {
    const file = fileEl.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/bids/${deal.id}/forms/${key}/reupload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.complete) { setFs(data.forms_status); toast.success("Completed & verified — form marked Filled."); reload?.(); }
      else { toast.error(`Still missing ${data.still_missing?.length || 0} field(s) — fill the marked boxes and re-upload.`); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Re-upload failed."); }
    fileEl.value = "";
  };
  const uploadBlank = async (fileEl) => {
    const file = fileEl.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      await api.post(`/bids/${deal.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.post(`/bids/${deal.id}/forms/process`);
      setFs(data); toast.success("Blank uploaded — the engine will fill it from your verified profile."); reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed."); }
    setBusy(false); fileEl.value = "";
  };
  const fetchBlank = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bids/${deal.id}/forms/fetch-blank`, {});
      setFs(data.forms_status);
      data.filled ? toast.success(data.message) : toast.error(data.message);
      reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fetch failed."); }
    setBusy(false);
  };

  if (!fs || !fs.forms?.length) {
    return (
      <div data-testid="forms-empty-wrap">
        <AmendmentsPanel deal={deal} reload={reload} />
        <div className="text-center py-10" data-testid="forms-empty">
          <FileCheck2 className="h-6 w-6 text-zinc-300 mx-auto mb-2" />
          <div className="text-sm text-zinc-500 mb-3">Forms package not built yet</div>
          <button onClick={process} disabled={busy} data-testid="forms-process-btn"
            className="text-xs bg-zinc-900 text-white px-3 py-1.5 rounded-md hover:bg-zinc-800 disabled:opacity-50">
            {busy ? "Processing…" : "Detect & fill forms"}</button>
        </div>
      </div>
    );
  }
  const pt = fs.pricing_template || {};
  const blockers = fs.blockers || [];
  const missingForms = (fs.forms || []).filter((f) => f.mandatory && (f.status === "Missing" || f.status === "Manual"));
  return (
    <div data-testid="forms-panel">
      <AmendmentsPanel deal={deal} reload={reload} />
      <div className={`flex items-center justify-between mb-3 px-3 py-2 rounded-md text-sm ${blockers.length ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-700"}`}>
        <span className="font-medium flex items-center gap-2">
          {blockers.length ? <AlertTriangle className="h-4 w-4" /> : <FileCheck2 className="h-4 w-4" />}
          {blockers.length ? `${blockers.length} package item(s) outstanding — Stage 7 locked` : "Package complete — all forms filled / covered"}
        </span>
        <span className="text-xs font-mono">{fs.filled_count}/{fs.total}</span>
      </div>

      {missingForms.length > 0 && (
        <div className="mb-4 border border-red-300 bg-red-50 rounded-lg p-3" data-testid="missing-required-forms">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-red-800 mb-1.5 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" /> {missingForms.length} required form(s) not produced — hard block
          </div>
          <div className="text-[12px] text-red-700/90 mb-2">
            The engine fills the solicitation's own form, else the canonical GSA blank (SF-1442/1449/33). These
            could not be produced — fetch the canonical blank, or upload the blank form to fill from your profile.
          </div>
          <button onClick={fetchBlank} disabled={busy} data-testid="fetch-blank-gsa-btn"
            className="mb-2 text-[11px] text-white bg-zinc-900 hover:bg-zinc-800 px-2.5 py-1 rounded-md disabled:opacity-50 inline-flex items-center gap-1">
            <Download className="h-3 w-3 text-gold" /> {busy ? "Fetching…" : "Fetch blank from GSA & fill"}
          </button>
          <div className="space-y-1.5">
            {missingForms.map((f, i) => (
              <div key={i} data-testid={`missing-form-${i}`} className="flex items-center justify-between gap-2 bg-muted/40 border border-red-200 rounded-md px-2.5 py-1.5">
                <div className="min-w-0">
                  <div className="text-[12px] font-medium text-zinc-800 truncate">{f.number || f.title}</div>
                  <div className="text-[10px] text-zinc-500 truncate">{f.source}</div>
                </div>
                <label data-testid={`missing-form-upload-${i}`} className="shrink-0 text-[11px] text-white bg-red-700 hover:bg-red-800 px-2 py-1 rounded-md cursor-pointer flex items-center gap-1">
                  <Upload className="h-3 w-3" /> Upload blank
                  <input type="file" accept="application/pdf" className="hidden" disabled={busy} onChange={(e) => uploadBlank(e.target)} />
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] text-zinc-400">SF-1442 auto-filled & merged · reps & certs via SAM · bid bond via surety</div>
        <div className="flex items-center gap-3">
          <button onClick={signAll} disabled={busy} data-testid="forms-sign-all-btn"
            title="Apply the authorized officer's e-signature + date to every signable form (BLACK). Signing does not send."
            className="text-[11px] flex items-center gap-1 text-white bg-zinc-900 hover:bg-zinc-800 px-2.5 py-1 rounded-md disabled:opacity-50">
            <PenLine className="h-3 w-3 text-gold" /> {busy ? "Signing…" : "Sign all forms"}</button>
          {(fs?.forms || []).some((f) => f.status === "Signed") && (
            <button onClick={unsignAll} disabled={busy} data-testid="forms-unsign-btn"
              title="Remove the applied e-signature and re-generate the forms unsigned. Restores the signature gate."
              className="text-[11px] flex items-center gap-1 text-zinc-500 hover:text-red-600 disabled:opacity-50">
              <RotateCcw className="h-3 w-3" /> Remove signature</button>
          )}
          <button onClick={process} disabled={busy} data-testid="forms-reprocess-btn"
            className="text-[11px] flex items-center gap-1 text-zinc-600 hover:text-zinc-900">
            <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} /> {busy ? "Processing…" : "Re-process"}</button>
        </div>
      </div>

      <div className={`mb-4 border rounded-lg p-3 ${pt.filled ? "border-emerald-200 bg-emerald-50/40" : "border-amber-300 bg-amber-50"}`} data-testid="forms-pricing-template">
        <div className="text-[11px] font-semibold uppercase tracking-wide mb-1 flex items-center gap-1.5">
          <DollarSign className="h-3.5 w-3.5" /> Pricing template {pt.provided_fillable ? "(government file filled)" : ""}
        </div>
        <div className="text-[12px] text-zinc-700">{pt.note || "—"}</div>
      </div>

      <div className="space-y-1.5">
        {fs.forms.map((f, i) => {
          const action = f.status === "Action required" || f.status === "Missing" || f.status === "Needs review" || f.status === "Manual";
          const canView = f.status === "Filled" || f.status === "Needs review" || f.merged;
          return (
            <div key={i} data-testid={`forms-row-${i}`} className={`border rounded-md px-3 py-2 ${action ? "border-amber-200 bg-amber-50/40" : "border-zinc-100"}`}>
              <div className="flex items-start gap-2.5">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-zinc-800 font-medium">{f.number || f.title}</div>
                  {f.number && f.title && <div className="text-[12px] text-zinc-600">{f.title}</div>}
                  <div className="text-[11px] text-zinc-500 mt-0.5">{f.source}</div>
                  {typeof f.confidence === "number" && (
                    <div className="text-[10px] text-zinc-400 mt-0.5" data-testid={`forms-confidence-${i}`}>Placement verified: {Math.round(f.confidence * 100)}%</div>
                  )}
                  {Array.isArray(f.flagged_fields) && f.flagged_fields.length > 0 && (
                    f.fingerprint ? (
                      <div className="mt-2 space-y-1.5" data-testid={`forms-flagged-${i}`}>
                        {f.flagged_fields.slice(0, 12).map((ff, j) => (
                          <div key={j} className="flex items-center gap-2 text-[11px]">
                            <span className="px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 shrink-0">{ff.label || "field"}</span>
                            <span className="text-zinc-400">→ map to</span>
                            <select data-testid={`forms-map-${i}-${j}`} defaultValue=""
                              onChange={(e) => e.target.value && mapCorrect(f.key, ff.label, e.target.value)}
                              className="text-[11px] border border-zinc-300 rounded px-1 py-0.5 bg-white">
                              <option value="">needs input…</option>
                              {CANON_KEYS.map((k) => <option key={k[0]} value={k[0]}>{k[1]}</option>)}
                            </select>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`forms-flagged-${i}`}>
                        {f.flagged_fields.slice(0, 8).map((ff, j) => (
                          <span key={j} className="text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800" title={ff.status}>
                            {ff.label || "field"} · needs input
                          </span>
                        ))}
                      </div>
                    )
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {canView && (
                    <a href={`${API_BASE}/bids/${deal.id}/forms/file/${f.key}`}
                      target="_blank" rel="noreferrer" data-testid={`forms-view-${i}`} className="text-zinc-400 hover:text-zinc-700"><ExternalLink className="h-3.5 w-3.5" /></a>
                  )}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${FORM_BADGE[f.status] || "bg-zinc-100 text-zinc-500"}`}>{f.status}</span>
                </div>
              </div>
              {f.xfa && (
                <div className="mt-2 flex flex-wrap items-center gap-3" data-testid={`forms-xfa-actions-${i}`}>
                  <button onClick={() => setXfaForm({ key: f.key, title: f.number || f.title })}
                    data-testid={`forms-xfa-preview-${i}`} className="text-[11px] text-zinc-700 underline hover:text-zinc-900 flex items-center gap-1">
                    <Eye className="h-3 w-3" /> Preview in browser
                  </button>
                  <a href={`${API_BASE}/bids/${deal.id}/forms/file/${f.key}`} target="_blank" rel="noreferrer"
                    data-testid={`forms-xfa-download-${i}`} className="text-[11px] text-zinc-700 underline hover:text-zinc-900 flex items-center gap-1">
                    <Download className="h-3 w-3" /> Download pre-filled draft
                  </a>
                  <a href="https://get.adobe.com/reader/" target="_blank" rel="noreferrer"
                    data-testid={`forms-xfa-adobe-${i}`} className="text-[11px] text-zinc-700 underline hover:text-zinc-900 flex items-center gap-1">
                    <ExternalLink className="h-3 w-3" /> Get free Adobe Reader
                  </a>
                  <label data-testid={`forms-xfa-reupload-${i}`} className="text-[11px] text-zinc-700 underline hover:text-zinc-900 cursor-pointer flex items-center gap-1">
                    <Upload className="h-3 w-3" /> Re-upload completed
                    <input type="file" accept="application/pdf" className="hidden" onChange={(e) => reupload(f.key, e.target)} />
                  </label>
                </div>
              )}
              {f.fingerprint && (f.status === "Needs review" || f.status === "Manual") && (
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <a href={`${API_BASE}/bids/${deal.id}/forms/best-attempt/${f.key}`} target="_blank" rel="noreferrer"
                    data-testid={`forms-best-attempt-${i}`} className="text-[11px] text-zinc-700 underline hover:text-zinc-900 flex items-center gap-1">
                    <Download className="h-3 w-3" /> Download best-attempt (flagged fields marked)
                  </a>
                  <label data-testid={`forms-reupload-${i}`} className="text-[11px] text-zinc-700 underline hover:text-zinc-900 cursor-pointer flex items-center gap-1">
                    <Upload className="h-3 w-3" /> Re-upload completed
                    <input type="file" accept="application/pdf" className="hidden" onChange={(e) => reupload(f.key, e.target)} />
                  </label>
                </div>
              )}
              {action && (
                attesting === f.key ? (
                  <div className="mt-2 flex items-center gap-2">
                    <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Bid bond from Swiftbonds, 20%"
                      data-testid={`forms-attest-note-${i}`} className="flex-1 text-[12px] border border-zinc-300 rounded px-2 py-1" />
                    <button onClick={() => attest(f.key)} data-testid={`forms-attest-save-${i}`} className="text-[11px] bg-zinc-900 text-white px-2 py-1 rounded">Mark filled</button>
                    <button onClick={() => { setAttesting(null); setNote(""); }} className="text-[11px] text-zinc-500">Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => setAttesting(f.key)} data-testid={`forms-attest-btn-${i}`}
                    className="mt-2 text-[11px] text-amber-700 underline hover:text-amber-900">Attest filled (bond/SAM/portal)</button>
                )
              )}
            </div>
          );
        })}
      </div>
      {xfaForm && (
        <XfaPreview dealId={deal.id} formKey={xfaForm.key} title={xfaForm.title} onClose={() => setXfaForm(null)} />
      )}
    </div>
  );
}


const COST_HIDE = ["ohp", "oh&p", "o&p", "overhead", "profit", "fee", "markup", "op"];
const COST_LABEL = { gc: "General conditions", subcontract: "Subcontract", sub: "Subcontract", equipment: "Equipment", labor: "Labor", material: "Material" };
const TO_SRC = {
  "published-bid-schedule": "Published · Bid Schedule", "published-measurement-payment": "Published · M&P",
  "drawing-schedule": "Drawing Schedule", "spec-quantity-table": "Spec Table",
  "computed-from-given-dimensions": "Computed (given dims)", "needs-measurement": "Needs Measurement",
  "measured": "Measured", "estimated": "Estimated", "photo-count": "Photo · count",
};
const TO_CONF = { high: "bg-emerald-100 text-emerald-700", medium: "bg-amber-100 text-amber-700", low: "bg-red-100 text-red-700" };

function ConfirmQty({ deal, item, unit, reload }) {
  const [open, setOpen] = React.useState(false);
  const [qty, setQty] = React.useState("");
  const [source, setSource] = React.useState("measured");
  const [basis, setBasis] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const submit = async () => {
    if (qty === "" || isNaN(Number(qty))) { toast.error("Enter a numeric quantity."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/bids/${deal.id}/quantities/confirm`,
        { item, quantity: Number(qty), source, basis });
      if (data?.nudge) toast.success(data.nudge, { duration: 6000 });
      else toast.success("Quantity confirmed — re-graded.");
      setOpen(false); setQty(""); setBasis("");
      reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Confirm failed."); }
    setBusy(false);
  };
  if (deal.pricing_locked) {
    return <div className="mt-1.5 text-[11px] text-zinc-400 inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Price locked</div>;
  }
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid={`confirm-qty-btn-${slug(item)}`}
        className="mt-1.5 text-[11px] inline-flex items-center gap-1 text-emerald-700 border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 rounded-md px-2 py-1">
        <CheckCircle2 className="h-3 w-3" /> Confirm quantity
      </button>
    );
  }
  return (
    <div className="mt-2 border border-emerald-200 bg-emerald-50/60 rounded-md p-2 space-y-1.5" data-testid={`confirm-qty-form-${slug(item)}`}>
      <div className="flex items-center gap-1.5">
        <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Quantity"
          data-testid={`confirm-qty-input-${slug(item)}`}
          className="w-28 text-xs border border-zinc-300 rounded px-2 py-1 font-mono" />
        <span className="text-[11px] text-zinc-500">{unit}</span>
        <select value={source} onChange={(e) => setSource(e.target.value)}
          data-testid={`confirm-qty-source-${slug(item)}`}
          className="text-xs border border-zinc-300 rounded px-1.5 py-1">
          <option value="measured">Measured</option>
          <option value="published">Published (gov)</option>
        </select>
      </div>
      <input value={basis} onChange={(e) => setBasis(e.target.value)} placeholder="Basis (sheet, SAM pull, sub quote…)"
        data-testid={`confirm-qty-basis-${slug(item)}`}
        className="w-full text-xs border border-zinc-300 rounded px-2 py-1" />
      <div className="flex items-center gap-2">
        <button onClick={submit} disabled={busy} data-testid={`confirm-qty-submit-${slug(item)}`}
          className="text-[11px] bg-emerald-600 text-white px-2.5 py-1 rounded hover:bg-emerald-700 disabled:opacity-50">
          {busy ? "Re-grading…" : "Confirm & re-grade"}</button>
        <button onClick={() => setOpen(false)} className="text-[11px] text-zinc-500 hover:text-zinc-800">Cancel</button>
      </div>
    </div>
  );
}
const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40);

function MeasureRegionModal({ deal, onClose, reload }) {
  const [sheets, setSheets] = React.useState(null);
  const [sp, setSp] = React.useState("");
  const [sheet, setSheet] = React.useState(1);
  const [box, setBox] = React.useState(null);   // {x0,y0,x1,y1} in displayed px
  const drag = React.useRef(null);
  const imgRef = React.useRef();
  const [result, setResult] = React.useState(null);
  const [scaleOverride, setScaleOverride] = React.useState("");
  const [item, setItem] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [calMode, setCalMode] = React.useState(false);
  const [calA, setCalA] = React.useState(null);
  const [calB, setCalB] = React.useState(null);
  const [calFeet, setCalFeet] = React.useState("");

  React.useEffect(() => {
    api.get(`/bids/${deal.id}/takeoff/sheets`).then(({ data }) => {
      setSheets(data); if (data.drawings?.[0]) setSp(data.drawings[0].storage_path);
    });
  }, [deal.id]);

  const reloadSheets = () => api.get(`/bids/${deal.id}/takeoff/sheets`).then(({ data }) => setSheets(data)).catch(() => {});
  const curCal = (sheets?.drawings || []).find((d) => d.storage_path === sp)?.calibrations?.find((c) => c.sheet === sheet);
  const clearCal = async () => {
    try {
      await api.post(`/bids/${deal.id}/takeoff/calibrate/clear`, { storage_path: sp, sheet });
      toast.success(`Sheet ${sheet} calibration cleared — measurements return to PENDING until recalibrated.`);
      setResult(null); reloadSheets(); reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Clear failed."); }
  };
  const [autoBusy, setAutoBusy] = useState(false);
  const autoScale = async () => {
    setAutoBusy(true);
    try {
      const { data } = await api.post(`/bids/${deal.id}/takeoff/calibrate`, { storage_path: sp, sheet, method: "auto" });
      toast.success(`Auto-detected scale on sheet ${sheet}: ${data.label || `${data.ppf} pts/ft`}. Measurements are now firm.`);
      setResult(null); reloadSheets(); reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "No scale could be auto-detected — use 2-point calibration."); }
    finally { setAutoBusy(false); }
  };

  const flagged = (deal.takeoff?.lines || []).filter((l) => l.source === "needs-measurement");
  const imgUrl = sp ? `${API_BASE}/bids/${deal.id}/takeoff/sheet-image?storage_path=${encodeURIComponent(sp)}&sheet=${sheet}` : "";

  const down = (e) => {
    const r = imgRef.current.getBoundingClientRect();
    const p = { x: e.clientX - r.left, y: e.clientY - r.top };
    if (calMode) {
      if (!calA || calB) { setCalA(p); setCalB(null); }
      else { setCalB(p); setCalMode(false); }
      return;
    }
    drag.current = p; setBox(null); setResult(null);
  };
  const move = (e) => { if (!drag.current) return; const r = imgRef.current.getBoundingClientRect(); setBox({ x0: drag.current.x, y0: drag.current.y, x1: e.clientX - r.left, y1: e.clientY - r.top }); };
  const up = () => { drag.current = null; };

  const measure = async () => {
    if (!box) { toast.error("Drag a box over the item on the drawing first."); return; }
    setBusy(true);
    try {
      const r = imgRef.current.getBoundingClientRect();
      const cal = (calA && calB && Number(calFeet) > 0)
        ? { x0: calA.x, y0: calA.y, x1: calB.x, y1: calB.y, known_feet: Number(calFeet) } : null;
      const { data } = await api.post(`/bids/${deal.id}/takeoff/measure-region`, {
        storage_path: sp, sheet, x0: box.x0, y0: box.y0, x1: box.x1, y1: box.y1,
        img_w: r.width, img_h: r.height, scale_override: scaleOverride, cal,
      });
      setResult(data);
      if (data.calibrated) reloadSheets();
      if (data.needs_scale) toast.message("Geometry measured — set the scale: either enter a scale (1/4\"=1') or use 2-point calibration on a known dimension.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Measure failed."); }
    setBusy(false);
  };

  const applyQty = async (val, unit) => {
    // Dir 88 — never auto-commit a mapping: if no pay item is chosen yet, fetch a UNIT-first suggestion,
    // prefill it (overridable), and let the officer tap the amount again to confirm.
    let clin = item;
    if (!clin) {
      try {
        const { data } = await api.post(`/bids/${deal.id}/quantities/suggest-clin`, { unit, item: "", firm: !!result?.firm });
        if (data.best) {
          setItem(data.best.item);
          toast.message(`Suggested CLIN: ${data.best.item} (${data.best.reason}). Tap the amount again to confirm, or pick a different pay item.`, { duration: 7000 });
          return;
        }
        toast.error("No CLIN matched — choose a pay item, or select “leave UNASSIGNED”.");
        return;
      } catch { /* fall through to confirm attempt */ }
    }
    try {
      const { data } = await api.post(`/bids/${deal.id}/quantities/confirm`, {
        item: clin === "__unassigned__" ? `Measured ${unit} · sheet ${sheet}` : clin,
        clin_item: clin, quantity: val, unit, source: "measured", firm: !!result?.firm,
        provenance: { storage_path: sp, sheet, ppf: result?.ppf, region: box ? [box.x0, box.y0, box.x1, box.y1] : null },
        basis: `Vector measure-region on sheet ${sheet}, scale ${result?.scale || scaleOverride} → ${val} ${unit}`,
      });
      toast.success(data.nudge || "Quantity confirmed from measured region.", { duration: 6000 });
      onClose(); reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Confirm failed."); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="measure-region-modal" onMouseUp={up}>
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[92vh] overflow-auto p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><Ruler className="h-5 w-5" /> Measure a region (free vector · PyMuPDF)</h3>
          <button onClick={onClose} data-testid="measure-close"><X className="h-5 w-5 text-zinc-400 hover:text-zinc-800" /></button>
        </div>
        {sheets === null && <div className="text-sm text-zinc-400">Loading drawings…</div>}
        {sheets && (sheets.drawings || []).length === 0 && <div className="text-sm text-zinc-400">No drawing PDFs attached to this deal.</div>}
        {sheets && (sheets.drawings || []).length > 0 && (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
              <select value={sp} onChange={(e) => { setSp(e.target.value); setBox(null); setResult(null); }} data-testid="measure-doc" className="border border-zinc-300 rounded px-2 py-1">
                {sheets.drawings.map((d) => <option key={d.storage_path} value={d.storage_path}>{d.filename} ({d.sheets} sheets)</option>)}
              </select>
              <label className="text-zinc-500">Sheet</label>
              <input type="number" min="1" value={sheet} onChange={(e) => { setSheet(Number(e.target.value) || 1); setBox(null); setResult(null); }} data-testid="measure-sheet" className="w-16 border border-zinc-300 rounded px-2 py-1" />
              <input placeholder={`scale e.g. 1/4"=1'`} value={scaleOverride} onChange={(e) => setScaleOverride(e.target.value)} data-testid="measure-scale" className="border border-zinc-300 rounded px-2 py-1 w-32" />
              <button onClick={() => { setCalMode(true); setCalA(null); setCalB(null); }} data-testid="measure-calibrate-btn"
                className={`rounded px-3 py-1 border ${calMode ? "bg-amber-500 text-white border-amber-500" : "border-zinc-300 hover:bg-zinc-100"}`}>
                {calMode ? "Click 2 pts on a known dimension…" : "Calibrate scale (2-pt)"}</button>
              <button onClick={autoScale} disabled={autoBusy} data-testid="measure-auto-scale-btn"
                title="Auto-detect the sheet's stated title-block scale or a graphic scale bar"
                className="rounded px-3 py-1 border border-sky-300 text-sky-700 hover:bg-sky-50 disabled:opacity-50">
                {autoBusy ? "Detecting…" : "Auto-detect scale"}</button>
              {(calA && calB) ? <input type="number" placeholder="known ft" value={calFeet} onChange={(e) => setCalFeet(e.target.value)} data-testid="measure-cal-feet" className="w-24 border border-amber-400 rounded px-2 py-1" /> : null}
              <button onClick={measure} disabled={busy} data-testid="measure-run" className="bg-zinc-900 text-white rounded px-3 py-1 hover:bg-zinc-800 disabled:opacity-50">{busy ? "Measuring…" : "Measure box"}</button>
              {curCal && (
                <span className="inline-flex items-center gap-1.5 rounded px-2 py-1 bg-emerald-50 border border-emerald-300 text-emerald-700" data-testid="sheet-calibrated-chip">
                  ✓ calibrated ({curCal.ppf} pts/ft)
                  <button onClick={clearCal} data-testid="clear-calibration-btn" title="Clear this sheet's scale — measurements return to PENDING until recalibrated"
                    className="ml-1 text-red-600 hover:text-red-800 underline">Clear / recalibrate</button>
                </span>
              )}
            </div>
            <div className="relative border border-zinc-200 rounded overflow-hidden select-none" style={{ cursor: "crosshair" }}
                 onMouseDown={down} onMouseMove={move}>
              <img ref={imgRef} src={imgUrl} alt="sheet" className="w-full block" draggable={false} data-testid="measure-image" />
              {box && <div className="absolute border-2 border-amber-500 bg-amber-300/20 pointer-events-none"
                style={{ left: Math.min(box.x0, box.x1), top: Math.min(box.y0, box.y1), width: Math.abs(box.x1 - box.x0), height: Math.abs(box.y1 - box.y0) }} />}
              {calA && <div className="absolute w-2 h-2 -ml-1 -mt-1 rounded-full bg-sky-600 pointer-events-none" style={{ left: calA.x, top: calA.y }} />}
              {calB && <div className="absolute w-2 h-2 -ml-1 -mt-1 rounded-full bg-sky-600 pointer-events-none" style={{ left: calB.x, top: calB.y }} />}
              {calA && calB && <svg className="absolute inset-0 w-full h-full pointer-events-none"><line x1={calA.x} y1={calA.y} x2={calB.x} y2={calB.y} stroke="#0284c7" strokeWidth="2" strokeDasharray="4" /></svg>}
            </div>
            <p className="text-[11px] text-zinc-400 mt-1">Drag a box around ONE pay item. No title-block scale? Click <b>Calibrate scale (2-pt)</b>, click the two ends of a dimension of known length, enter its feet — the engine derives the scale and cross-checks it (&gt;2% flag).</p>
            {result && (
              <div className="mt-3 border border-emerald-200 bg-emerald-50/50 rounded-md p-3 text-sm" data-testid="measure-result">
                {result.measured_lf != null || result.measured_sf != null ? (
                  <>
                    <div className="font-medium text-zinc-800">Measured (scale {result.scale}):</div>
                    {result.calibration_check ? (
                      <div className={`mt-1 text-[11px] ${result.calibration_check.within_2pct ? "text-emerald-700" : "text-red-600 font-medium"}`} data-testid="measure-cal-check">
                        Scale-discipline check: calibrated {result.calibration_check.calibrated_ppf} vs stated {result.calibration_check.stated_scale} ({result.calibration_check.stated_ppf}) — Δ{result.calibration_check.delta_pct}% · {result.calibration_check.note}
                      </div>
                    ) : null}
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      {result.measured_lf != null && <button onClick={() => applyQty(result.measured_lf, "LF")} data-testid="use-lf" className="border border-emerald-300 rounded px-2 py-1 hover:bg-emerald-100">{result.measured_lf} LF →</button>}
                      {result.measured_sf != null && <button onClick={() => applyQty(result.measured_sf, "SF")} data-testid="use-sf" className="border border-emerald-300 rounded px-2 py-1 hover:bg-emerald-100">{result.measured_sf} SF →</button>}
                      <select value={item} onChange={(e) => setItem(e.target.value)} data-testid="measure-item" className="border border-zinc-300 rounded px-2 py-1 text-xs max-w-xs">
                        <option value="">— assign to pay item —</option>
                        {flagged.map((l, i) => <option key={i} value={l.item}>{l.item.slice(0, 60)}</option>)}
                        <option value="__unassigned__">— leave UNASSIGNED —</option>
                      </select>
                      {!result.firm && <span className="text-[11px] text-amber-700 font-medium" data-testid="measure-pending-note">PENDING — not calibrated; won't create a firm price</span>}
                    </div>
                  </>
                ) : (
                  <div className="text-amber-700">Measured {result.raw_length_pts} pts of line / {result.raw_area_pts2} pts² of area in the box. Enter the sheet scale above and re-measure to convert to LF/SF.</div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}


function Takeoff({ deal, reload }) {
  const [to, setTo] = React.useState(deal.takeoff || null);
  const [busy, setBusy] = React.useState(false);
  const [measuring, setMeasuring] = React.useState(false);
  React.useEffect(() => { setTo(deal.takeoff || null); }, [deal.takeoff]);
  const run = async () => {
    setBusy(true);
    try {
      await api.post(`/bids/${deal.id}/takeoff/run`);
      const t0 = Date.now();
      const poll = async () => {
        const { data } = await api.get(`/bids/${deal.id}/takeoff`);
        if (data && data.generated_at && (!to || data.generated_at !== to.generated_at)) { setTo(data); setBusy(false); toast.success("Takeoff complete."); reload?.(); return; }
        if (Date.now() - t0 > 150000) { setBusy(false); toast.error("Takeoff timed out."); return; }
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Takeoff failed."); setBusy(false); }
  };

  if (!to || to.applicable === null) {
    return (
      <div className="text-center py-10" data-testid="takeoff-empty">
        <Ruler className="h-6 w-6 text-zinc-300 mx-auto mb-2" />
        <div className="text-sm text-zinc-500 mb-3">No takeoff yet — runs automatically in Stage 4 for construction bids with drawings.</div>
        <button onClick={run} disabled={busy} data-testid="takeoff-run-btn"
          className="text-xs bg-zinc-900 text-white px-3 py-1.5 rounded-md hover:bg-zinc-800 disabled:opacity-50">
          {busy ? "Running…" : "Run takeoff"}</button>
      </div>
    );
  }
  if (to.applicable === false) {
    return <div className="text-sm text-zinc-500 py-8 text-center" data-testid="takeoff-na">{to.detection?.detected || "Not a construction / drawing-set bid — takeoff skipped."}</div>;
  }
  const lines = to.lines || [];
  const flags = to.flags || [];
  return (
    <div data-testid="takeoff-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] text-zinc-600 flex-1">{to.detection?.detected}</div>
        <div className="flex items-center gap-2 shrink-0">
          {!deal.pricing_locked && (
            <button onClick={async () => {
              try {
                const { data } = await api.post(`/bids/${deal.id}/rfq/share`);
                try { await navigator.clipboard.writeText(data.link); } catch (_) {}
                toast.success(`RFQ link copied — ${data.code}`, { description: data.link, duration: 8000 });
              } catch (e) { toast.error(e?.response?.data?.detail || "Share failed."); }
            }} data-testid="share-rfq-btn"
              className="text-[11px] flex items-center gap-1 text-zinc-700 border border-zinc-300 hover:bg-zinc-50 rounded-md px-2 py-1">
              <Share2 className="h-3 w-3" /> Share RFQ</button>
          )}
          {!deal.pricing_locked && (
            <button onClick={() => setMeasuring(true)} data-testid="measure-region-btn"
              className="text-[11px] flex items-center gap-1 text-zinc-700 border border-zinc-300 hover:bg-zinc-50 rounded-md px-2 py-1">
              <Ruler className="h-3 w-3" /> Measure region</button>
          )}
          {!deal.pricing_locked && (to.published_count || 0) > 0 && (
            <button onClick={async () => {
              try {
                const { data } = await api.post(`/bids/${deal.id}/confirm-published-items`);
                toast.success(data.nudge || `Confirmed ${data.confirmed} published item(s).`, { duration: 6000 });
                reload?.();
              } catch (e) { toast.error(e?.response?.data?.detail || "Confirm failed."); }
            }} data-testid="confirm-published-btn"
              className="text-[11px] flex items-center gap-1 text-emerald-700 border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 rounded-md px-2 py-1">
              <CheckCircle2 className="h-3 w-3" /> Confirm published</button>
          )}
          <a href={`${API_BASE}/bids/${deal.id}/takeoff.xlsx`} target="_blank" rel="noreferrer" data-testid="takeoff-export"
            className="text-[11px] flex items-center gap-1 text-zinc-600 hover:text-zinc-900 border border-zinc-200 rounded-md px-2 py-1"><Download className="h-3 w-3" /> Excel</a>
          <button onClick={run} disabled={busy} data-testid="takeoff-rerun-btn"
            className="text-[11px] flex items-center gap-1 text-zinc-600 hover:text-zinc-900"><RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} /> {busy ? "Running…" : "Re-run"}</button>
        </div>
      </div>
      <div className="flex items-center gap-3 mb-3 text-[11px] flex-wrap">
        <span className="px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-700">{to.published_count || 0} published</span>
        <span className="px-2 py-0.5 rounded-sm bg-teal-100 text-teal-700">{to.measured_count || 0} measured</span>
        <span className="px-2 py-0.5 rounded-sm bg-sky-100 text-sky-700">{(to.computed_count || 0) + (to.estimated_count || 0)} estimated</span>
        <span className="px-2 py-0.5 rounded-sm bg-red-100 text-red-700">{to.needs_measurement_count || 0} need measurement</span>
        {(to.cad_count || 0) > 0 && <span className="px-2 py-0.5 rounded-sm bg-indigo-100 text-indigo-700" data-testid="takeoff-cad-count">{to.cad_count} CAD</span>}
        {(to.photo_count || 0) > 0 && <span className="px-2 py-0.5 rounded-sm bg-fuchsia-100 text-fuchsia-700" data-testid="takeoff-photo-count">{to.photo_count} photo</span>}
        {to.sdk_available ? <span className="text-emerald-600">· Apryse measurement on</span> : <span className="text-emerald-600">· free vector measurement (PyMuPDF)</span>}
      </div>
      {(to.photo_scope?.length > 0) && (
        <div className="mb-3 border border-fuchsia-200 bg-fuchsia-50/50 rounded-md px-3 py-2" data-testid="takeoff-photo-scope">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-fuchsia-700 mb-1">Photo scope evidence (vision — verify)</div>
          {to.photo_scope.map((s, i) => <div key={i} className="text-[11px] text-zinc-700">{s}</div>)}
        </div>
      )}
      {flags.length > 0 && (
        <div className="mb-3 border border-amber-300 bg-amber-50 rounded-md px-3 py-2" data-testid="takeoff-flags">
          {flags.map((f, i) => <div key={i} className="text-[12px] text-amber-800 flex gap-1.5"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />{f}</div>)}
        </div>
      )}
      {(to.schedules?.length || to.csi_coverage?.length || to.cross_checks?.length) ? (
        <div className="mb-3 grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="takeoff-reconciliation">
          <div className="border border-zinc-200 rounded-md px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">Schedules parsed (§2)</div>
            {(to.schedules || []).length ? (to.schedules || []).map((s, i) => (
              <div key={i} className="text-[11px] text-zinc-700 truncate">{s.schedule_type} · {(s.rows || []).length} rows</div>
            )) : <div className="text-[11px] text-zinc-400">none found</div>}
          </div>
          <div className="border border-zinc-200 rounded-md px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">CSI coverage (§3)</div>
            {(to.csi_coverage || []).length ? (to.csi_coverage || []).map((c, i) => (
              <div key={i} className={`text-[11px] truncate ${c.flag ? "text-red-600 font-medium" : "text-zinc-700"}`}>div {c.division} · {c.status}</div>
            )) : <div className="text-[11px] text-zinc-400">no CSI divisions detected</div>}
          </div>
          <div className="border border-zinc-200 rounded-md px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">Cross-checks (§12)</div>
            {(to.cross_checks || []).map((c, i) => (
              <div key={i} className={`text-[11px] truncate ${c.result === "FLAG" ? "text-red-600 font-medium" : "text-zinc-700"}`}>{c.result} · {c.check}</div>
            ))}
          </div>
        </div>
      ) : null}
      <div className="space-y-1.5">
        {lines.map((ln, i) => {
          const nm = ln.source === "needs-measurement";
          const canConfirm = nm || ln.source === "estimated";
          return (
            <div key={i} data-testid={`takeoff-row-${i}`} className={`border rounded-md px-3 py-2 ${nm ? "border-amber-200 bg-amber-50/40" : "border-zinc-100"}`}>
              <div className="flex items-start gap-2.5">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-zinc-800">{ln.item}</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">{ln.basis || ln.status}</div>
                  {canConfirm && <ConfirmQty deal={deal} item={ln.item} unit={ln.unit} reload={reload} />}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-mono text-zinc-900">{ln.quantity != null ? Number(ln.quantity).toLocaleString() : "—"} <span className="text-zinc-400 text-xs">{ln.unit}</span></div>
                  <div className="flex items-center gap-1 justify-end mt-0.5">
                    <span className="text-[10px] text-zinc-500">{TO_SRC[ln.source] || ln.method}</span>
                    <span className={`text-[10px] px-1 rounded-sm ${TO_CONF[ln.confidence] || "bg-zinc-100 text-zinc-500"}`}>{ln.confidence}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {measuring && <MeasureRegionModal deal={deal} onClose={() => setMeasuring(false)} reload={reload} />}
    </div>
  );
}


function SubQuotes({ deal, reload }) {
  const [quotes, setQuotes] = React.useState(null);
  React.useEffect(() => {
    api.get(`/bids/${deal.id}/sub-quotes`).then(({ data }) => setQuotes(data.quotes || [])).catch(() => setQuotes([]));
  }, [deal.id]);
  const pending = (quotes || []).filter((q) => q.status === "pending");
  if (!quotes || !quotes.length) return null;
  const apply = async (qid) => {
    try {
      const { data } = await api.post(`/bids/${deal.id}/sub-quotes/${qid}/apply`);
      toast.success(`Applied to ${data.applied} line(s) — total ${data.amount}.`, { duration: 6000 });
      reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Apply failed."); }
  };
  return (
    <div className="mb-4 border border-amber-200 bg-amber-50/40 rounded-md p-3" data-testid="sub-quotes">
      <div className="text-[11px] uppercase tracking-wider text-amber-700 mb-2">Sub quotes ({pending.length} pending)</div>
      {(quotes).map((q) => (
        <div key={q.id} className="flex items-center justify-between gap-2 text-sm py-1" data-testid={`sub-quote-${q.id}`}>
          <span className="min-w-0 truncate text-zinc-700">{q.vendor} · {q.lines?.length || 0} line(s){q.status === "applied" ? <span className="text-emerald-600"> · applied</span> : ""}</span>
          {q.status === "pending" && (
            <button onClick={() => apply(q.id)} data-testid={`apply-sub-quote-${q.id}`}
              className="shrink-0 text-[11px] bg-emerald-600 text-white rounded px-2 py-1 hover:bg-emerald-700">Apply to pricing</button>
          )}
        </div>
      ))}
    </div>
  );
}

const RateCardSchedule = ({ deal }) => {
  const rows = deal.rate_schedule || [];
  const meta = deal.rate_schedule_meta || {};
  const softFlags = deal.pricing_inputs_missing || [];
  return (
    <div data-testid="rate-card-schedule">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <div className="text-[11px] font-semibold text-sky-700 uppercase tracking-wide">Vol IV — Fixed Labor Rates (IDIQ / rate card)</div>
          <div className="text-2xl font-display font-bold text-zinc-900">Fully-burdened labor-rate schedule</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wider text-zinc-400">IDIQ ceiling (not WD's bid amount)</div>
          <div className="text-xl font-display font-bold text-zinc-700 tnum" data-testid="idiq-ceiling">{deal.idiq_ceiling_display || "—"}</div>
        </div>
      </div>
      <div className="text-[12px] text-zinc-500 mb-3">
        This is an indefinite-delivery vehicle: the offer is a schedule of fully-burdened hourly rates competed per
        task order — not a lump-sum project total. {deal.pricing_model_reason || ""}
      </div>
      {rows.length > 0 ? (
        <div className="rounded-lg border border-zinc-200 overflow-hidden">
          <table className="w-full text-[12px]" data-testid="rate-schedule-table">
            <thead className="bg-zinc-50 text-zinc-500 uppercase text-[10px] tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Labor category</th>
                <th className="text-right px-3 py-2">Fully-burdened rate</th>
                <th className="text-left px-3 py-2 border-l border-dashed border-zinc-300">Cockpit: base + burden</th>
                <th className="text-left px-3 py-2">Cockpit: grounded source</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-zinc-100">
                  {/* CUSTOMER-FACING: category + clean rate only */}
                  <td className="px-3 py-2 font-medium text-zinc-800">{r.category}</td>
                  <td className="px-3 py-2 text-right font-bold text-zinc-900 tnum" data-testid={`rate-${i}`}>${r.burdened_rate}/hr</td>
                  {/* COCKPIT-ONLY (source/confidence) — not part of the customer schedule */}
                  <td className="px-3 py-2 text-[11px] text-zinc-500 border-l border-dashed border-zinc-200 tnum">
                    ${r.base_wage}/hr × {r.burden_factor} + {r.ohp_pct}% OH&amp;P {r.soc ? `· SOC ${r.soc}` : ""}
                  </td>
                  <td className="px-3 py-2 text-[11px]">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase mr-1 ${r.confidence === "grounded" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{r.confidence === "grounded" ? "Grounded" : "Benchmark"}</span>
                    <span className="text-zinc-500">{r.wage_source}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-[12px] text-amber-800">
          Labor-rate schedule not built yet — run Pricing (Stage 4) to generate the burdened rate table for each labor category.
        </div>
      )}
      {meta.source_summary && (
        <div className="mt-2 text-[11px] text-zinc-400" data-testid="rate-card-source-summary">Cockpit basis — {meta.source_summary}</div>
      )}
      {softFlags.length > 0 && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="rate-card-flags">
          <div className="text-[11px] font-bold text-amber-800 uppercase tracking-wide mb-1">Cockpit note (not a send blocker)</div>
          <ul className="space-y-1">
            {softFlags.slice(0, 5).map((f, i) => (
              <li key={i} className="text-[12px] text-amber-900">• {f}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const Pricing = ({ deal, reload }) => {
  const [refineOpen, setRefineOpen] = React.useState(false);
  // Dir 178 — per-line document-backing status (map of cost-line index -> grounded bool) for the officer's
  // "Grounded ✓ / Unsupported ⚠" badge. Fetched live so it is accurate for every bid, not just re-priced ones.
  const [scopeGrounding, setScopeGrounding] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    if (!deal?.id || !(deal.cost_model || []).length) { setScopeGrounding(null); return; }
    api.get(`/bids/${deal.id}/pricing/scope-grounding`)
      .then(({ data }) => {
        if (!alive) return;
        const m = {}; (data.lines || []).forEach((r) => { m[r.index] = r.grounded; });
        setScopeGrounding(m);
      })
      .catch(() => { if (alive) setScopeGrounding(null); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal?.id, deal?.updated_at, (deal.cost_model || []).length]);
  // Dir 191c — per-line COST-BAND grounding evidence (map of cost-line index -> {band, source}) so a reviewer
  // sees EXACTLY why a line grounded: 'Grounded via [band] · [cited source]'. Fetched live like the doc-backing
  // badge, so it is accurate for every bid, not just re-priced ones.
  const [lineGrounding, setLineGrounding] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    if (!deal?.id || !(deal.cost_model || []).length) { setLineGrounding(null); return; }
    api.get(`/bids/${deal.id}/pricing/line-grounding`)
      .then(({ data }) => {
        if (!alive) return;
        const m = {}; (data.lines || []).forEach((r) => { if (r.band) m[r.index] = { band: r.band, source: r.source, source_url: r.source_url }; });
        setLineGrounding(m);
      })
      .catch(() => { if (alive) setLineGrounding(null); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal?.id, deal?.updated_at, (deal.cost_model || []).length]);
  // Dir 187 — one-tap jump from the "N lines unsupported" pill to the first flagged cost line (+ brief flash).
  const lineRefs = React.useRef({});
  const flashTimer = React.useRef(null);
  const [flashLine, setFlashLine] = React.useState(null);
  React.useEffect(() => () => { if (flashTimer.current) clearTimeout(flashTimer.current); }, []);
  // Dir 188 — fix or remove an unsupported line inline (operator estimate / remove) without leaving the tab.
  const [fixLine, setFixLine] = React.useState(null);   // index whose operator-estimate editor is open
  const [fixRate, setFixRate] = React.useState("");
  const [fixNote, setFixNote] = React.useState("");
  const [fixBusy, setFixBusy] = React.useState(false);
  // Directive 41 — a RATE_CARD (IDIQ / T&M) bid's deliverable is a burdened labor-RATE schedule, not a total.
  if (deal.pricing_model === "RATE_CARD") return <RateCardSchedule deal={deal} />;
  if (!deal.amount) return <Placeholder stage="4" what="Pricing" />;
  const lines = Object.entries(deal.costs || {}).filter(([k]) => !COST_HIDE.includes(k.toLowerCase().replace(/\s/g, "")));
  const model = deal.cost_model || [];
  const missing = deal.pricing_inputs_missing || [];
  const locked = !!deal.pricing_locked;
  // Dir 186 — aggregate count of cost lines whose scope isn't backed by the ingested docs (per-line badge below).
  const unsupportedCount = scopeGrounding ? Object.values(scopeGrounding).filter((g) => g === false).length : 0;
  const firstUnsupportedIndex = scopeGrounding
    ? Object.keys(scopeGrounding).map(Number).filter((k) => scopeGrounding[k] === false).sort((a, b) => a - b)[0]
    : undefined;
  const scrollToUnsupported = () => {
    if (firstUnsupportedIndex === undefined) return;
    const el = lineRefs.current[firstUnsupportedIndex];
    if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    setFlashLine(firstUnsupportedIndex);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlashLine(null), 2200);
  };
  const openFix = (i) => { setFixLine(i); setFixRate(""); setFixNote(""); };
  const submitOperatorEstimate = async (i, unit) => {
    const rate = parseFloat(String(fixRate).replace(/[^0-9.]/g, ""));
    if (!rate || rate <= 0) { toast.error(`Enter a $ / ${unit || "unit"} rate.`); return; }
    setFixBusy(true);
    try {
      await api.post(`/bids/${deal.id}/pricing/line-grounding/supply-comp`,
        { line_index: i, mode: "operator", unit_price: rate, reason: fixNote || undefined });
      toast.success("Line grounded with your estimate.");
      setFixLine(null); setFixRate(""); setFixNote("");
      reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not apply the estimate."); }
    finally { setFixBusy(false); }
  };
  const removeLine = async (i, item) => {
    if (!window.confirm(`Remove "${item}" from the cost model? The total will be recalculated.`)) return;
    setFixBusy(true);
    try {
      const { data } = await api.post(`/bids/${deal.id}/pricing/line/remove`, { line_index: i });
      toast.success(`Line removed — new total ${data.amount}.`);
      setFixLine(null);
      reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove the line."); }
    finally { setFixBusy(false); }
  };
  const toggleLock = async () => {
    try {
      const { data } = await api.post(`/bids/${deal.id}/pricing/lock`, { locked: !locked });
      toast.success(data.pricing_locked ? `Price locked at ${data.amount} — frozen.` : "Pricing unlocked — you can re-price.");
      reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Lock failed."); }
  };
  return (
    <div data-testid="pricing-preview">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <div className="text-3xl font-display font-bold text-zinc-900">{deal.amount}</div>
          {unsupportedCount > 0 && (
            <button type="button" onClick={scrollToUnsupported} data-testid="pricing-unsupported-count"
              title="Jump to the first cost line whose scope isn't backed by the ingested documents."
              className="mt-1.5 inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[11px] font-semibold bg-amber-100 text-amber-700 border border-amber-200 hover:bg-amber-200 transition-colors cursor-pointer">
              <AlertTriangle className="h-3 w-3" /> {unsupportedCount} {unsupportedCount === 1 ? "line" : "lines"} unsupported
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <a href={`${API_BASE}/bids/${deal.id}/export.xlsx`} target="_blank" rel="noreferrer" data-testid="bid-export-xlsx"
            className="text-[11px] inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 border border-zinc-300 text-zinc-600 hover:bg-zinc-50">
            <Download className="h-3 w-3" /> Export to Excel
          </a>
          <button onClick={() => setRefineOpen(true)} data-testid="refine-lines-btn"
            className="text-[11px] inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100">
            <AlertTriangle className="h-3 w-3" /> Refine estimated lines
          </button>
          <button onClick={toggleLock} data-testid="pricing-lock-btn"
            className={`text-[11px] inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 border ${
              locked ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-zinc-300 text-zinc-600 hover:bg-zinc-50"}`}>
            <Lock className="h-3 w-3" /> {locked ? "Price locked — unlock" : "Accept & lock price"}
          </button>
        </div>
      </div>
      {locked && (
        <div className="mb-2 text-[11px] text-emerald-700" data-testid="pricing-locked-badge">
          Frozen at {deal.pricing_locked_amount || deal.amount}{deal.pricing_locked_by ? ` · accepted by ${deal.pricing_locked_by}` : ""} — re-runs keep this number until unlocked.
        </div>
      )}
      {deal.pricing_v2 && (() => {
        const norm = (s) => String(s || "").replace(/[^0-9.]/g, "");
        const v2 = deal.pricing_v2;
        const diverges = v2.amount && deal.amount && norm(v2.amount) !== norm(deal.amount);
        return v2.locked ? (
          <div className="mb-2 text-[11px] rounded-md border border-emerald-300 bg-emerald-50 text-emerald-800 px-2.5 py-1.5" data-testid="pricing-v2-status">
            <span className="font-semibold">Locked (Engine v2): {v2.amount}</span> — this is the accepted, canonical bid total everywhere (frozen; explicit unlock + re-run required to pick up new data).
          </div>
        ) : (
          <div className="mb-2 text-[11px] rounded-md border border-amber-300 bg-amber-50 text-amber-800 px-2.5 py-1.5" data-testid="pricing-v2-status">
            {diverges ? (
              <><span className="font-semibold">Two totals — accept to reconcile:</span> Accepted total: <span className="font-semibold">{deal.amount}</span> · Engine v2 re-run available: <span className="font-semibold">{v2.amount}</span> (not yet accepted). Accept the re-run to make it the one canonical total.</>
            ) : (
              <><span className="font-semibold">Engine v2 re-run: {v2.amount}</span> — matches the current total; accept to lock it in.</>
            )}
          </div>
        );
      })()}
      <div className="text-[11px] text-zinc-400 mb-4 font-mono">reconciled total · computed in code (qty × rate × markup) · markup distributed into line items (no O&amp;P line)</div>
      {deal.pricing_schedule?.summary && (
        <div className="mb-4 text-[11px] text-zinc-600 border border-zinc-200 rounded-md px-2.5 py-1.5" data-testid="pricing-schedule">{deal.pricing_schedule.summary}</div>
      )}
      {(deal.options_total > 0 || deal.alternates_total > 0) && (
        <div className="mb-4 flex gap-3 text-[11px]" data-testid="pricing-groups">
          {deal.options_total > 0 && <span className="px-2 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200">Options (separate): ${Number(deal.options_total).toLocaleString()}</span>}
          {deal.alternates_total > 0 && <span className="px-2 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200">Alternates (separate): ${Number(deal.alternates_total).toLocaleString()}</span>}
        </div>
      )}
      {!locked && <SubQuotes deal={deal} reload={reload} />}

      {missing.length > 0 && (
        <div className="mb-4 border border-amber-300 bg-amber-50 rounded-lg p-3" data-testid="pricing-missing">
          <div className="text-[11px] font-semibold text-amber-800 uppercase tracking-wide mb-1.5 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Inputs to confirm before submission (not grounded)</div>
          <ul className="text-[12px] text-amber-900 space-y-1 list-disc pl-4">
            {missing.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
      )}

      {model.length > 0 && (
        <div className="mb-4 border border-zinc-200 rounded-lg overflow-hidden" data-testid="cost-model">
          <table className="w-full text-[12px]">
            <thead><tr className="bg-zinc-50 text-zinc-500 text-[10px] uppercase tracking-wide">
              <th className="text-left px-2.5 py-1.5">Item</th><th className="text-right px-2 py-1.5">Qty</th>
              <th className="text-left px-1 py-1.5">Unit</th><th className="text-right px-2 py-1.5">Unit rate</th>
              <th className="text-right px-2.5 py-1.5">Extended</th></tr></thead>
            <tbody>
              {model.map((r, i) => (
                <tr key={i} ref={(el) => { lineRefs.current[i] = el; }}
                  className={`border-t border-zinc-100 align-top transition-colors ${flashLine === i ? "bg-amber-100" : ""}`}
                  data-testid={`cost-line-${i}`}>
                  <td className="px-2.5 py-1.5 text-zinc-800">{r.item}
                    {scopeGrounding && scopeGrounding[i] === false ? (
                      <div className="mt-1" data-testid={`scope-unsupported-wrap-${i}`}>
                        <div className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300"
                          data-testid={`scope-unsupported-${i}`}
                          title="This line's scope is NOT grounded in the ingested solicitation documents — verify it is real, document-backed scope or remove it before sending (NO FABRICATION).">
                          <AlertTriangle className="h-3 w-3" /> Unsupported
                        </div>
                        {!locked && (
                          <div className="mt-1.5 flex flex-wrap items-center gap-2" data-testid={`fix-line-actions-${i}`}>
                            {fixLine === i ? (
                              <div className="flex flex-wrap items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-md p-1.5" data-testid={`fix-line-editor-${i}`}>
                                <span className="text-[10px] text-amber-800">$ /{(r.unit || "unit")}</span>
                                <input type="number" step="any" value={fixRate} onChange={(e) => setFixRate(e.target.value)}
                                  placeholder="rate" data-testid={`fix-line-rate-${i}`}
                                  className="w-20 text-[11px] px-1.5 py-0.5 border border-amber-300 rounded" />
                                <input type="text" value={fixNote} onChange={(e) => setFixNote(e.target.value)}
                                  placeholder="basis (optional)" data-testid={`fix-line-note-${i}`}
                                  className="w-40 text-[11px] px-1.5 py-0.5 border border-amber-300 rounded" />
                                <button disabled={fixBusy} onClick={() => submitOperatorEstimate(i, r.unit)} data-testid={`fix-line-save-${i}`}
                                  className="text-[11px] font-semibold px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">Save estimate</button>
                                <button disabled={fixBusy} onClick={() => setFixLine(null)} data-testid={`fix-line-cancel-${i}`}
                                  className="text-[11px] px-2 py-0.5 rounded border border-zinc-300 text-zinc-600 hover:bg-zinc-50">Cancel</button>
                              </div>
                            ) : (
                              <>
                                <button onClick={() => openFix(i)} data-testid={`fix-line-estimate-btn-${i}`}
                                  className="text-[10px] font-semibold px-2 py-0.5 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100">
                                  Set operator estimate
                                </button>
                                <button disabled={fixBusy} onClick={() => removeLine(i, r.item)} data-testid={`fix-line-remove-btn-${i}`}
                                  className="text-[10px] font-semibold px-2 py-0.5 rounded-md border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50">
                                  Remove line
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    ) : scopeGrounding && scopeGrounding[i] === true ? (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300"
                        data-testid={`scope-grounded-${i}`}
                        title="This line's scope is backed by the ingested solicitation documents.">
                        <CheckCircle2 className="h-3 w-3" /> Grounded
                      </div>
                    ) : null}
                    {r.line_grounding === "resolved_from_docs" && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300"
                        data-testid={`cost-line-resolved-${i}`}
                        title={`Quantity auto-resolved from the ingested documents: ${r.quantity_source || ""}`}>
                        <FileCheck2 className="h-3 w-3" />
                        auto-resolved from {r.quantity_source || "ingested docs"}
                      </div>
                    )}
                    {lineGrounding && lineGrounding[i]?.band && (
                      lineGrounding[i].source_url ? (
                        <a href={lineGrounding[i].source_url} target="_blank" rel="noreferrer"
                          className="mt-1 inline-flex items-start gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 hover:bg-emerald-200 hover:border-emerald-400 transition-colors max-w-[560px] cursor-pointer"
                          data-testid={`cost-line-grounded-via-${i}`}
                          title={`Open the cited comparable cost source in a new tab to verify this line's grounding: ${lineGrounding[i].band}${lineGrounding[i].source ? " — " + lineGrounding[i].source : ""}`}>
                          <FileCheck2 className="h-3 w-3 mt-[1px] shrink-0" />
                          <span>Grounded via {lineGrounding[i].band}{lineGrounding[i].source ? ` · ${lineGrounding[i].source}` : ""}</span>
                          <ExternalLink className="h-3 w-3 mt-[1px] shrink-0 opacity-70" />
                        </a>
                      ) : (
                        <div className="mt-1 inline-flex items-start gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 max-w-[560px]"
                          data-testid={`cost-line-grounded-via-${i}`}
                          title={`This line's price is grounded against a cited comparable cost band: ${lineGrounding[i].band}${lineGrounding[i].source ? " — " + lineGrounding[i].source : ""}`}>
                          <FileCheck2 className="h-3 w-3 mt-[1px] shrink-0" />
                          <span>Grounded via {lineGrounding[i].band}{lineGrounding[i].source ? ` · ${lineGrounding[i].source}` : ""}</span>
                        </div>
                      )
                    )}
                    {r.source && <div className="text-[10px] text-zinc-400">{String(r.source).startsWith("ESTIMATE") || String(r.source).startsWith("not grounded") || String(r.source).startsWith("NOT GROUNDED") ? <span className="text-amber-700 font-semibold">{r.source}</span> : r.source}</div>}
                    {r.fallback_method && <div className="text-[10px] text-amber-700" data-testid={`cost-line-fallback-${i}`}>method: {r.fallback_method}{r.qty_basis ? ` · ${r.qty_basis}` : ""}</div>}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-zinc-600">{r.quantity != null ? Number(r.quantity).toLocaleString() : "—"}</td>
                  <td className="px-1 py-1.5 text-zinc-500">{r.unit || ""}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-zinc-600">{r.unit_rate != null ? `$${Number(r.unit_rate).toLocaleString()}` : "—"}</td>
                  <td className="px-2.5 py-1.5 text-right font-mono text-zinc-900">{r.extended != null ? `$${Number(r.extended).toLocaleString()}` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="space-y-2 text-sm">
        <div className="text-[10px] uppercase tracking-wide text-zinc-400">Roll-up</div>
        {lines.map(([k, v]) => (
          <div key={k} className="flex justify-between border-b border-zinc-100 py-1.5">
            <span className="text-zinc-500">{COST_LABEL[k.toLowerCase()] || k.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())}</span>
            <span className="text-zinc-900 font-mono">${Number(v).toLocaleString()}</span>
          </div>
        ))}
      </div>
      {deal.pricing_notes && <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed whitespace-pre-wrap">{deal.pricing_notes}</p>}
      {refineOpen && <RefineLinesModal deal={deal} onClose={() => setRefineOpen(false)} reload={reload} />}
    </div>
  );
};

function RefineLinesModal({ deal, onClose, reload }) {
  const [data, setData] = React.useState(null);
  const [drafts, setDrafts] = React.useState({});
  const [busy, setBusy] = React.useState(null);
  const load = React.useCallback(() => {
    api.get(`/bids/${deal.id}/refine-lines`).then(({ data }) => setData(data)).catch(() => setData({ lines: [], remaining: 0 }));
  }, [deal.id]);
  React.useEffect(() => { load(); }, [load]);

  const setDraft = (id, k) => (e) => setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), [k]: e.target.value } }));

  const confirm = async (line) => {
    const dr = drafts[line.line_id] || {};
    const rate = parseFloat(dr.rate ?? line.unit_rate);
    const basis = (dr.basis || "").trim();
    if (!rate || rate <= 0) return toast.error("Enter a real unit rate (> 0).");
    if (!basis) return toast.error("A basis (source or note) is required — no blank clear.");
    setBusy(line.line_id);
    try {
      const { data: res } = await api.post(`/bids/${deal.id}/refine-lines/confirm`,
        { line_id: line.line_id, unit_rate: rate, basis });
      setData((d) => ({ ...d, ...res }));
      const gate = (res.pricing_blockers || []).length === 0
        ? (res.remaining === 0 ? "Pricing leg is now GREEN." : "") : "";
      toast.success(`Reconciled total ${res.total} · ${res.remaining} line(s) left. ${gate}`, { duration: 6000 });
      reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Confirm failed."); }
    finally { setBusy(null); }
  };

  const lines = data?.lines || [];
  const otherBlockers = [...((data?.other_blockers?.compliance) || []), ...((data?.other_blockers?.forms) || [])];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="w-full max-w-3xl bg-white border border-zinc-200 rounded-xl p-6 max-h-[88vh] overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()} data-testid="refine-lines-modal">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-display font-bold text-xl text-zinc-900 flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" /> Refine estimated lines</h2>
          <button onClick={onClose} data-testid="refine-close"><X className="h-5 w-5 text-zinc-400 hover:text-zinc-900" /></button>
        </div>
        <p className="text-[12px] text-zinc-500 mb-3">Confirm or replace each not-grounded rate with a real rate + basis. The ONE reconciled total re-computes after every edit.</p>
        <div className="flex items-center justify-between mb-3 text-sm">
          <span className="text-zinc-500">Reconciled total: <span className="font-semibold text-zinc-900" data-testid="refine-total">{data?.total || deal.amount}</span></span>
          <span data-testid="refine-remaining" className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${lines.length === 0 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
            {lines.length === 0 ? "0 not-grounded lines — pricing leg clear" : `${lines.length} not-grounded line(s) left`}
          </span>
        </div>

        {data?.pricing_locked && (
          <div className="mb-3 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">Pricing is locked — unlock it on the Pricing tab to refine lines.</div>
        )}

        {lines.length === 0 && (
          <div className="text-center py-8" data-testid="refine-empty">
            <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
            <div className="text-sm text-zinc-700 font-medium">No not-grounded lines remain — pricing is fully grounded.</div>
          </div>
        )}

        <div className="space-y-3">
          {lines.map((line) => {
            const dr = drafts[line.line_id] || {};
            return (
              <div key={line.line_id} data-testid={`refine-line-${line.line_id}`} className="border border-amber-200 bg-amber-50/40 rounded-lg p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-zinc-900">{line.item}</div>
                    <div className="text-[11px] text-amber-700 mt-0.5">method: {line.method}{line.basis ? ` · ${line.basis}` : ""}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">Qty {line.quantity != null ? Number(line.quantity).toLocaleString() : "—"} {line.unit} · placeholder rate ${line.unit_rate != null ? Number(line.unit_rate).toLocaleString() : "—"} · extended ${line.extended != null ? Number(line.extended).toLocaleString() : "—"}</div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr_auto] gap-2 mt-2.5 items-end">
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500">Real unit rate ($)</span>
                    <input type="number" step="any" data-testid={`refine-rate-${line.line_id}`}
                      value={dr.rate ?? line.unit_rate ?? ""} onChange={setDraft(line.line_id, "rate")}
                      className="w-full mt-1 border border-zinc-300 rounded-md px-2.5 py-1.5 text-sm outline-none focus:border-amber-400" />
                  </label>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500">Basis (source or note) — required</span>
                    <input type="text" data-testid={`refine-basis-${line.line_id}`}
                      value={dr.basis || ""} onChange={setDraft(line.line_id, "basis")}
                      placeholder="e.g. Sub quote — Acme Marine 6/2026; or RSMeans 31 23 16"
                      className="w-full mt-1 border border-zinc-300 rounded-md px-2.5 py-1.5 text-sm outline-none focus:border-amber-400" />
                  </label>
                  <button onClick={() => confirm(line)} disabled={busy === line.line_id} data-testid={`refine-confirm-${line.line_id}`}
                    className="h-[34px] px-3 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">
                    {busy === line.line_id ? "…" : "Confirm"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {(data && lines.length === 0 && otherBlockers.length > 0) && (
          <div className="mt-4 border border-zinc-200 rounded-lg p-3" data-testid="refine-other-blockers">
            <div className="text-[11px] font-semibold text-zinc-700 uppercase tracking-wide mb-1.5">Pricing is green, but the send gate is still RED ({otherBlockers.length})</div>
            <ul className="text-[12px] text-zinc-600 space-y-1 list-disc pl-4">
              {otherBlockers.slice(0, 6).map((b, i) => <li key={i}>{b}</li>)}
            </ul>
            <div className="text-[10px] text-zinc-400 mt-1.5">Clearing placeholder rates only clears the pricing leg. Compliance, forms, and the wage determination are satisfied independently.</div>
          </div>
        )}
      </div>
    </div>
  );
}

const Capture = ({ deal }) => deal.capture_plan
  ? <div><div className="text-xs text-zinc-900 font-mono mb-2">Win themes</div><p className="text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap">{deal.capture_plan}</p></div>
  : <Placeholder stage="3" what="Capture plan" />;

function Outline({ deal }) {
  const sections = deal.outline || [];
  const pb = deal.page_budget || {};
  if (!sections.length && !(pb.rows || []).length) return <Placeholder stage="3" what="Annotated outline + page budget" />;
  return (
    <div className="space-y-5" data-testid="outline-preview">
      <div>
        <div className="text-xs text-zinc-900 font-mono mb-2">Annotated outline · Section L order</div>
        <div className="space-y-2">
          {sections.map((s, i) => (
            <div key={i} className="border border-zinc-200 rounded-md p-2.5">
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-900 font-medium">{s.number} {s.title}</span>
                <span className="text-[11px] text-zinc-500">Target {s.page_target}p / Limit {s.page_limit}p</span>
              </div>
              {s.l_ref && <div className="text-[11px] text-sky-700 mt-0.5" data-testid={`outline-lref-${i}`}>Section L: {s.l_ref}</div>}
              {s.m_factor && <div className="text-[11px] text-zinc-500 mt-0.5">Section M: {s.m_factor}</div>}
            </div>
          ))}
        </div>
      </div>
      {(pb.rows || []).length > 0 && (
        <div>
          <div className="text-xs text-zinc-900 font-mono mb-2">Page budget {pb.volume_cap ? `(cap ${pb.volume_cap}p)` : ""}</div>
          <table className="w-full text-xs">
            <thead><tr className="text-zinc-400 border-b border-zinc-200"><th className="text-left py-1.5">Section</th><th>Limit</th><th>Target</th></tr></thead>
            <tbody>{pb.rows.map((r, i) => (<tr key={i} className="border-b border-zinc-100"><td className="py-1.5 text-zinc-700">{r.section}</td><td className="text-center text-zinc-500">{r.page_limit}</td><td className="text-center text-zinc-900">{r.page_target}</td></tr>))}</tbody>
            <tfoot><tr className="text-zinc-900 font-semibold"><td className="py-1.5">Estimated</td><td></td><td className="text-center text-zinc-900">{pb.estimated_pages}p</td></tr></tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

function Shred({ deal, reload }) {
  const sh = deal.shred || {};
  const reqs = sh.requirements || [];
  const docs = sh.documents_reviewed || [];
  const flags = sh.flags || [];
  const [filter, setFilter] = useState("All");
  const [busy, setBusy] = useState(false);

  const reshred = async () => {
    setBusy(true);
    try {
      await api.post(`/bids/${deal.id}/shred`);
      toast.success("Re-shredding in the background — refreshing shortly…");
      setTimeout(() => { reload?.(); setBusy(false); }, 30000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Shred failed."); setBusy(false); }
  };

  const counts = reqs.reduce((a, r) => { a[r.status] = (a[r.status] || 0) + 1; return a; }, {});
  const shown = filter === "All" ? reqs : reqs.filter((r) => r.status === filter);
  const [visCount, sentinelRef] = useIncremental(shown.length, 60);
  const shownLimited = shown.slice(0, visCount);
  const byCat = shownLimited.reduce((a, r) => { (a[r.category] = a[r.category] || []).push(r); return a; }, {});
  const statuses = ["All", "GAP", "Partial", "Not Started", "Covered"];

  return (
    <div data-testid="shred-tab">
      <MissingDrawingsSpecsBanner items={sh.missing_drawings_specs} className="mb-3" />
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-xs text-zinc-900 font-mono flex items-center gap-2">
          <Scissors className="h-3.5 w-3.5" /> {reqs.length} requirements · {docs.length} documents reviewed
        </div>
        <div className="flex items-center gap-2">
          {reqs.length > 0 && <a href={`${API_BASE}/bids/${deal.id}/shred.csv`} className="flex items-center gap-1 text-[11px] text-zinc-600 hover:text-zinc-900" data-testid="shred-export"><Download className="h-3.5 w-3.5" /> Export Excel</a>}
          <button onClick={reshred} disabled={busy} data-testid="shred-rerun" className="flex items-center gap-1 text-[11px] text-zinc-600 hover:text-zinc-900 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Re-shred
          </button>
        </div>
      </div>

      {/* Documents reviewed */}
      <div className="mb-4 border border-zinc-200 rounded-md p-3" data-testid="documents-reviewed">
        <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-2">Documents reviewed</div>
        {sh.completeness && (
          <div className="mb-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5 flex items-start gap-1.5" data-testid="docs-completeness">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {sh.completeness}
          </div>
        )}
        {docs.length === 0 && <div className="text-xs text-zinc-400">No documents pulled yet.</div>}
        <div className="space-y-1">
          {docs.map((d, i) => (
            <div key={i} className="flex items-center justify-between text-xs" data-testid={`doc-reviewed-${i}`}>
              <span className="text-zinc-700 flex items-center gap-1.5 min-w-0"><FileText className="h-3 w-3 text-zinc-400 shrink-0" /> <span className="truncate">{d.name}</span></span>
              {d.reviewed ? <span className="text-emerald-600 flex items-center gap-1 shrink-0"><CheckCircle2 className="h-3 w-3" /> reviewed</span>
                : d.status === "no_text"
                  ? <span className="text-amber-600 flex items-center gap-1 shrink-0" title={d.flag}><AlertTriangle className="h-3 w-3" /> retrieved · no text</span>
                  : <span className="text-red-600 flex items-center gap-1 shrink-0" title={d.flag}><AlertTriangle className="h-3 w-3" /> not retrieved</span>}
            </div>
          ))}
        </div>
      </div>

      {flags.length > 0 && (
        <div className="mb-4 border border-amber-200 bg-amber-50/60 rounded-md p-3" data-testid="shred-flags">
          <div className="text-[11px] uppercase tracking-wider text-amber-700 mb-1.5 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> CO questions / ambiguities</div>
          <ul className="space-y-1">{flags.map((f, i) => <li key={i} className="text-xs text-amber-800">• {f}</li>)}</ul>
        </div>
      )}

      {reqs.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-[180px] text-center">
          <Scissors className="h-6 w-6 text-zinc-300 mb-2" />
          <div className="text-sm text-zinc-500">{sh.note || "Anvil shreds the solicitation automatically once documents are pulled."}</div>
          <button onClick={reshred} disabled={busy} className="mt-3 text-xs bg-zinc-900 text-white px-3 py-1.5 rounded-md hover:bg-zinc-800 disabled:opacity-50">{busy ? "Shredding…" : "Run shred now"}</button>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid="shred-filters">
            {statuses.map((s) => (
              <button key={s} onClick={() => setFilter(s)} data-testid={`shred-filter-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${filter === s ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300"}`}>
                {s}{s !== "All" && counts[s] ? ` ${counts[s]}` : ""}
              </button>
            ))}
          </div>
          <div className="space-y-4">
            {Object.entries(byCat).map(([cat, rows]) => (
              <div key={cat} data-testid={`shred-cat-${cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">{cat} · {rows.length}</div>
                <div className="space-y-1.5">
                  {rows.map((r) => {
                    const st = REQ_STATUS[r.status] || REQ_STATUS["Not Started"];
                    const SI = st.I;
                    return (
                      <div key={r.id} data-testid={`shred-req-${r.id}`} className="border border-zinc-200 rounded-md px-3 py-2">
                        <div className="flex items-start gap-2">
                          <span className="text-sm text-zinc-800 flex-1">{r.text}{r.mandatory && <span className="ml-1.5 text-[9px] bg-zinc-100 text-zinc-500 px-1 py-0.5 rounded-sm align-middle">MUST</span>}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border flex items-center gap-1 shrink-0 ${st.c}`}><SI className="h-3 w-3" /> {r.status}</span>
                        </div>
                        <div className="text-[11px] text-zinc-400 mt-1">src: {r.source_citation || "—"}{r.where_addressed && <span className="text-zinc-500"> · ↳ {r.where_addressed}</span>}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          {visCount < shown.length && <div ref={sentinelRef} data-testid="shred-more" className="py-3 text-center text-[11px] text-zinc-400">Loading more… ({visCount}/{shown.length})</div>}
        </>
      )}
    </div>
  );
}